<template>
  <div class="document-list">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>{{ pageTitle }}</span>
      </div>

      <!-- 搜索栏 -->
      <el-form :inline="true" class="search-form">
        <el-form-item>
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索税号/姓名/批次号"
            clearable
            @clear="getDocumentList">
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-select v-model="confirmStatusFilter" placeholder="确认状态" clearable @change="getDocumentList" style="width: 120px;">
            <el-option label="全部" :value="null"></el-option>
            <el-option label="未确认" :value="0"></el-option>
            <el-option label="已确认" :value="1"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <div ref="buttonContainer" class="button-container">
            <!-- 搜索按钮：始终显示 -->
            <el-button type="primary" @click="getDocumentList">
              <i class="el-icon-search"></i> 搜索
            </el-button>
            
            <!-- 动态显示的按钮 -->
            <template v-for="(btn, index) in visibleButtons">
              <el-button 
                :key="btn.key"
                :type="btn.type" 
                :disabled="btn.disabled"
                @click="btn.onClick"
                v-if="btn.visible">
                <i :class="btn.icon"></i> {{ btn.label }}
              </el-button>
            </template>
            
            <!-- 更多操作下拉菜单 -->
            <el-dropdown v-if="dropdownButtons.length > 0" @command="handleMoreCommand" style="margin-left: 10px;">
              <el-button type="primary">
                更多操作<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <template v-for="(btn, index) in dropdownButtons">
                  <el-dropdown-item 
                    :key="btn.key"
                    :command="btn.command"
                    :disabled="btn.disabled"
                    v-if="btn.visible">
                    <i :class="btn.icon"></i> {{ btn.label }}
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-form-item>
      </el-form>

      <!-- 表格 -->
      <el-table 
        :data="documentList" 
        style="width: 100%"
        border
        stripe
        v-loading="loading"
        row-key="id"
        @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column prop="serialNumber" label="序号" width="80" sortable align="center"></el-table-column>
        <el-table-column prop="batchNumber" label="批次号" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="taxNumber" label="税号" width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="fullName" label="姓名" width="250" show-overflow-tooltip></el-table-column>
        <el-table-column prop="dateOfBirth" label="日期" width="120" align="center"></el-table-column>
        <el-table-column prop="confirmStatus" label="确认状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.confirmStatus === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.confirmStatus === 1 ? '已确认' : '未确认' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="address" label="地址" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column label="操作" width="350" fixed="right" align="center" class-name="operation-column">
          <template slot-scope="scope">
            <el-button 
              size="small" 
              icon="el-icon-view" 
              @click="viewDocument(scope.row)"
              v-if="hasPermission('document:list')">
              查看
            </el-button>
            <el-button 
              size="small" 
              type="primary" 
              icon="el-icon-edit" 
              @click="editDocument(scope.row)"
              v-if="hasPermission('document:edit')">
              编辑
            </el-button>
            <el-button 
              size="small" 
              type="warning" 
              icon="el-icon-document" 
              @click="exportJpg(scope.row)"
              v-if="hasPermission('document:export')">
              {{ getButtonName('document:export', '生成证书') }}
            </el-button>
            <el-button 
              size="small" 
              type="danger" 
              icon="el-icon-delete" 
              @click="deleteDocument(scope.row)"
              v-if="hasPermission('document:delete')">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.pageNum"
        :page-sizes="[10, 20, 50, 100, 200, 300, 500, 1000]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        style="margin-top: 20px; text-align: center">
      </el-pagination>
    </el-card>

    <!-- 编辑/查看弹窗 -->
    <el-dialog 
      :title="dialogMode === 'view' ? '查看证件信息' : (currentDocument.id ? '编辑证件信息' : '新增证件信息')" 
      :visible.sync="dialogVisible" 
      width="90%"
      top="2vh"
      :close-on-click-modal="false">
      <el-row :gutter="20">
        <!-- 左侧：证件图片 -->
        <el-col :span="10">
          <div class="image-section">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <h3 style="margin: 0; color: #303133;">🖼️ 证件图片</h3>
              <div v-if="dialogMode === 'edit'">
                <input 
                  type="file" 
                  ref="imageUpload"
                  accept="image/*"
                  style="display: none;"
                  @change="handleLocalImageUpload">
                <el-button 
                  size="small" 
                  type="primary" 
                  icon="el-icon-upload"
                  @click="$refs.imageUpload.click()">
                  {{ currentDocument.imageUrl ? '替换图片' : '上传图片' }}
                </el-button>
                <el-button 
                  v-if="currentDocument.imageUrl"
                  size="small" 
                  type="success" 
                  icon="el-icon-view"
                  :loading="recognizing"
                  @click="recognizeCurrentImage">
                  {{ recognizing ? '识别中...' : '开始识别' }}
                </el-button>
              </div>
            </div>
            <div class="image-container" v-if="currentDocument.imageUrl">
              <div class="magnifier-wrapper" 
                @mousemove="handleMouseMove"
                @mouseenter="showMagnifier = true"
                @mouseleave="showMagnifier = false"
                :style="{ 
                  overflow: 'auto', 
                  maxHeight: '70vh', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px'
                }">
                <el-image
                  ref="documentImage"
                  :src="getImageUrl(currentDocument.imageUrl)"
                  :style="{ 
                    transform: `rotate(${imageRotation}deg) scale(${imageScale})`, 
                    transformOrigin: 'center center',
                    maxWidth: 'none', 
                    width: '100%',
                    cursor: magnifierEnabled ? 'crosshair' : 'pointer',
                    transition: 'transform 0.3s ease'
                  }"
                  fit="contain"
                  :preview-src-list="[getImageUrl(currentDocument.imageUrl)]"
                  @error="handleImageError">
                  <div slot="error" class="image-error">
                    <i class="el-icon-picture-outline"></i>
                    <p>图片加载失败</p>
                  </div>
                </el-image>
                
                <!-- 放大镜 -->
                <div 
                  v-show="showMagnifier && magnifierEnabled"
                  class="magnifier-glass"
                  :style="magnifierStyle">
                  <img 
                    :src="getImageUrl(currentDocument.imageUrl)"
                    :style="magnifierImageStyle"
                    alt="放大视图">
                </div>
              </div>
            </div>
            <div v-else class="no-image">
              <i class="el-icon-picture-outline"></i>
              <p>暂无图片</p>
            </div>
            
            <!-- 图片操作按钮 -->
            <div class="image-controls" v-if="currentDocument.imageUrl" style="margin-top: 10px;">
              <!-- 图片缩放滑块 -->
              <div style="margin-bottom: 10px; padding: 10px; background-color: #f5f7fa; border-radius: 4px;">
                <div style="display: flex; align-items: center;">
                  <span style="font-size: 12px; color: #606266; margin-right: 10px; min-width: 80px;">图片缩放：{{ Math.round(imageScale * 100) }}%</span>
                  <el-slider 
                    v-model="imageScalePercent" 
                    :min="50" 
                    :max="200" 
                    :step="10"
                    @change="handleScaleChange"
                    style="flex: 1; margin-right: 10px;">
                  </el-slider>
                  <el-button size="mini" @click="resetScale">
                    <i class="el-icon-refresh"></i> 重置
                  </el-button>
                </div>
              </div>
              
              <el-button-group>
                <el-button size="small" icon="el-icon-refresh-left" @click="rotateImage(-90)">
                  左旋转
                </el-button>
                <el-button size="small" icon="el-icon-refresh-right" @click="rotateImage(90)">
                  右旋转
                </el-button>
                <el-button size="small" icon="el-icon-refresh" @click="resetRotation">
                  重置
                </el-button>
                <el-button 
                  size="small" 
                  :icon="magnifierEnabled ? 'el-icon-zoom-out' : 'el-icon-zoom-in'" 
                  :type="magnifierEnabled ? 'primary' : ''"
                  @click="toggleMagnifier">
                  {{ magnifierEnabled ? '关闭放大镜' : '开启放大镜' }}
                </el-button>
              </el-button-group>
              
              <!-- 放大镜设置 -->
              <div v-if="magnifierEnabled" style="margin-top: 10px; padding: 10px; background-color: #f5f7fa; border-radius: 4px;">
                <div style="margin-bottom: 8px;">
                  <span style="font-size: 12px; color: #606266; margin-right: 10px;">镜片大小：{{ magnifierSize }}px</span>
                  <el-button-group size="mini">
                    <el-button @click="adjustMagnifierSize(-20)" :disabled="magnifierSize <= 100">
                      <i class="el-icon-minus"></i>
                    </el-button>
                    <el-button @click="adjustMagnifierSize(20)" :disabled="magnifierSize >= 300">
                      <i class="el-icon-plus"></i>
                    </el-button>
                  </el-button-group>
                </div>
                <div>
                  <span style="font-size: 12px; color: #606266; margin-right: 10px;">放大倍数：{{ magnifierZoom }}x</span>
                  <el-button-group size="mini">
                    <el-button @click="adjustMagnifierZoom(-0.5)" :disabled="magnifierZoom <= 1.5">
                      <i class="el-icon-minus"></i>
                    </el-button>
                    <el-button @click="adjustMagnifierZoom(0.5)" :disabled="magnifierZoom >= 5">
                      <i class="el-icon-plus"></i>
                    </el-button>
                  </el-button-group>
                </div>
              </div>
            </div>
          </div>
        </el-col>
        
        <!-- 右侧：编辑表单 -->
        <el-col :span="14">
          <div class="form-section" :class="{ 'monospace-font': useMonospaceFont }">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <h3 style="margin: 0; color: #303133;">✏️ 证件信息编辑</h3>
              <div>
                <el-switch
                  v-model="useMonospaceFont"
                  active-text="等宽字体"
                  inactive-text="关闭"
                  :disabled="dialogMode === 'view'"
                  style="margin-right: 15px;">
                </el-switch>
                <el-switch
                  v-model="autoUpperCase"
                  active-text="自动转大写"
                  inactive-text="关闭"
                  :disabled="dialogMode === 'view'"
                  style="margin-right: 15px;">
                </el-switch>
                <el-button 
                  v-if="dialogMode === 'edit'"
                  size="mini" 
                  type="primary" 
                  icon="el-icon-document-copy"
                  @click="replaceClipboardWithN"
                  :loading="replacingClipboard">
                  替换为Ñ
                </el-button>
                <el-button 
                  v-if="dialogMode === 'edit'"
                  size="mini" 
                  type="warning" 
                  icon="el-icon-refresh"
                  @click="reparseDocument" 
                  :loading="reparsing"
                  style="margin-left: 10px;">
                  重新解析
                </el-button>
              </div>
            </div>
            <el-form :model="currentDocument" label-width="100px" ref="documentForm" size="small">
              <el-form-item label="批次号" prop="batchNumber">
                <el-input v-model="currentDocument.batchNumber" :disabled="true" placeholder="自动生成"></el-input>
              </el-form-item>
              <el-form-item label="序号" prop="serialNumber">
                <el-input-number 
                  v-model="currentDocument.serialNumber" 
                  :min="1" 
                  :disabled="dialogMode === 'view'"
                  style="width: 100%">
                </el-input-number>
              </el-form-item>
              <el-form-item label="税号" prop="taxNumber">
                <el-input v-model="currentDocument.taxNumber" :disabled="dialogMode === 'view'" @input="autoConfirm"></el-input>
              </el-form-item>
              <el-form-item label="姓名1" prop="surname">
                <el-input 
                  ref="surnameInput"
                  v-model="currentDocument.surname" 
                  :disabled="dialogMode === 'view'" 
                  @input="handleFieldInput('surname', $event)"
                  @blur="handleFieldBlur('surname')">
                  <template slot="prepend">
                    <el-button 
                      icon="el-icon-document-copy" 
                      :disabled="dialogMode === 'view'"
                      @click="copyFullNameToSurname"
                      title="复制姓名到姓名1"
                      class="copy-fullname-btn">
                    </el-button>
                  </template>
                </el-input>
              </el-form-item>
              <el-form-item label="姓名2" prop="givenName">
                <el-input 
                  ref="givenNameInput"
                  v-model="currentDocument.givenName" 
                  :disabled="dialogMode === 'view'" 
                  @input="handleFieldInput('givenName', $event)"
                  @blur="handleFieldBlur('givenName')">
                </el-input>
              </el-form-item>
              <el-form-item label="姓名3" prop="middleName">
                <el-input 
                  ref="middleNameInput"
                  v-model="currentDocument.middleName" 
                  :disabled="dialogMode === 'view'" 
                  @input="handleFieldInput('middleName', $event)"
                  @blur="handleFieldBlur('middleName')">
                </el-input>
              </el-form-item>
              <el-form-item label="姓名" prop="fullName">
                <el-input 
                  ref="fullNameInput"
                  v-model="currentDocument.fullName" 
                  :disabled="dialogMode === 'view'" 
                  @input="handleFieldInput('fullName', $event)"
                  @blur="handleFieldBlur('fullName')">
                </el-input>
              </el-form-item>
              <el-form-item label="出生日期" prop="dateOfBirth">
                <el-input v-model="currentDocument.dateOfBirth" :disabled="dialogMode === 'view'" @input="markAsConfirmed"></el-input>
              </el-form-item>
              <el-form-item label="地址" prop="address">
                <el-input 
                  ref="addressInput"
                  type="textarea" 
                  :rows="3" 
                  v-model="currentDocument.address"
                  :disabled="dialogMode === 'view'"
                  @input="handleFieldInput('address', $event)"
                  @blur="handleFieldBlur('address')">
                </el-input>
              </el-form-item>
              
              <el-form-item label="确认状态" type="confirmStatus">
                <div style="display: flex; align-items: center; gap: 15px;">
                  <el-radio-group v-model="currentDocument.confirmStatus" :disabled="dialogMode === 'view'">
                    <el-radio :label="0">未确认</el-radio>
                    <el-radio :label="1">已确认</el-radio>
                  </el-radio-group>
                  <div style="flex: 1; display: flex; justify-content: center;">
                    <el-button 
                      type="success" 
                      size="small"
                      icon="el-icon-check"
                      @click="confirmDocument" 
                      v-if="dialogMode === 'edit'"
                      :loading="confirming">
                      已确认
                    </el-button>
                  </div>
                </div>
              </el-form-item>
              
              <!-- 原始识别文本 -->
              <el-form-item label="原始文本" prop="rawText">
                <el-input 
                  type="textarea" 
                  :rows="3" 
                  v-model="currentDocument.rawText"
                  placeholder="原始识别文本"
                  readonly
                  style="font-family: monospace; font-size: 12px;">
                </el-input>
              </el-form-item>
            </el-form>
          </div>
        </el-col>
      </el-row>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveDocumentEdit" v-if="dialogMode === 'edit'" :loading="saving">
          <i class="el-icon-finished"></i> 保 存
        </el-button>
      </div>
    </el-dialog>
    
    <!-- 批量识别弹窗 -->
    <el-dialog :title="getBatchDialogTitle()" :visible.sync="batchDialogVisible" width="700px" :close-on-click-modal="false">
      <el-form :model="batchForm" label-width="120px" v-if="batchDialogMode === 'recognize'">
        <el-form-item label="文件夹路径">
          <el-input 
            v-model="batchForm.folderPath" 
            placeholder="例如: D:\\soft"
            style="width: 100%"
            readonly>
            <template slot="append">
              <el-button icon="el-icon-folder" @click="triggerFolderSelect">选择文件夹</el-button>
            </template>
          </el-input>
          <input 
            ref="folderInput" 
            type="file" 
            webkitdirectory 
            directory 
            multiple 
            style="display: none;" 
            @change="handleFolderSelect" />
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            点击"选择文件夹"按钮，选择包含按序号命名的子文件夹的目录
          </div>
        </el-form-item>
        
        <!-- 文件夹统计信息（改为按钮） -->
        <el-form-item label="文件夹统计" v-if="folderStats.length > 0">
          <el-button type="info" size="small" @click="showFolderStatsDialog">
            <i class="el-icon-folder-opened"></i>
            查看详细统计 (共{{ folderStats.length }}个文件夹, {{ totalFilesCount }}个文件)
            <el-badge v-if="abnormalFolders.length > 0 || missingFolders.length > 0" 
              :value="abnormalFolders.length + missingFolders.length" 
              type="warning" 
              style="margin-left: 10px;">
            </el-badge>
          </el-button>
        </el-form-item>
        
        <el-form-item label="图片关键词">
          <el-input v-model="batchForm.imageKeyword" placeholder="默认: FRONT_ID" @input="updateFolderStats"></el-input>
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            只识别文件名包含此关键词的图片
          </div>
        </el-form-item>
      </el-form>
      
      <!-- 批量导出JPG到指定目录模式 -->
      <el-form :model="exportFolderForm" label-width="120px" v-if="batchDialogMode === 'exportToFolder' && !batchProcessing && batchProgress === 0">
        <el-alert
          title="功能说明"
          type="info"
          :closable="false"
          style="margin-bottom: 20px;">
          <div style="line-height: 1.8; font-size: 13px;">
            请先点击下方按钮选择您电脑上的目标文件夹，然后点击开始导出。<br/>
            系统将会在该文件夹下自动创建序号子文件夹（如 1/1.jpg, 2/2.jpg）。<br/>
            <span style="color: #E6A23C; font-weight: bold;">⚠️ 注意：请选择一个非空文件夹（或在空文件夹中创建一个临时文件），否则无法选择。</span>
          </div>
        </el-alert>
        
        <el-form-item label="目标文件夹">
          <el-input 
            v-model="exportFolderForm.displayPath" 
            placeholder="请点击右侧按钮选择文件夹"
            readonly
            style="width: 100%">
            <template slot="append">
              <el-button icon="el-icon-folder" @click="triggerTargetFolderSelect">选择文件夹</el-button>
            </template>
          </el-input>
          <input 
            ref="targetFolderInput" 
            type="file" 
            webkitdirectory 
            directory 
            style="display: none;" 
            @change="handleTargetFolderSelect" />
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            支持本地部署和云服务器部署两种场景，文件将自动保存到您选择的本地文件夹。
          </div>
        </el-form-item>
      </el-form>
      
      <!-- 批量导出JPG到本地目录模式 -->
      <el-form :model="exportLocalFolderForm" label-width="120px" v-if="batchDialogMode === 'exportToLocalFolder' && !batchProcessing && batchProgress === 0">
        <el-alert
          title="功能说明"
          type="success"
          :closable="false"
          style="margin-bottom: 20px;">
          <div style="line-height: 1.8; font-size: 13px;">
            请先点击下方按钮选择您电脑上的目标文件夹，然后点击开始导出。<br/>
            系统将逐个生成JPG图片并直接保存到指定文件夹（如 1/1.jpg, 2/2.jpg）。<br/>
            <span style="color: #67C23A; font-weight: bold;">✅ 优势：适合本地部署，实时显示生成进度，不需要ZIP压缩和解压步骤。</span>
          </div>
        </el-alert>
        
        <el-form-item label="目标文件夹">
          <el-input 
            v-model="exportLocalFolderForm.displayPath" 
            placeholder="请点击右侧按钮选择文件夹"
            readonly
            style="width: 100%">
            <template slot="append">
              <el-button icon="el-icon-folder-add" @click="triggerLocalFolderSelect">选择文件夹</el-button>
            </template>
          </el-input>
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            需要现代浏览器支持（Chrome/Edge），文件将逐个生成并直接保存到本地文件夹。
          </div>
        </el-form-item>
      </el-form>
      
      <div v-if="batchProcessing || batchProgress > 0" style="margin-top: 20px;">
        <el-progress :percentage="batchProgress" :status="batchStatus"></el-progress>
        <div style="margin-top: 10px; color: #606266; font-weight: bold;">
          {{ batchMessage }}
        </div>
        <el-divider></el-divider>
        <div style="max-height: 400px; overflow-y: auto; background-color: #f5f7fa; padding: 15px; border-radius: 4px;">
          <el-timeline>
            <el-timeline-item 
              v-for="(log, index) in batchLogs" 
              :key="index"
              :timestamp="log.time"
              :type="log.type"
              :icon="getLogIcon(log)"
              placement="top">
              <span :style="getLogStyle(log)">{{ log.message }}</span>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeBatchDialog">关 闭</el-button>
        <el-button v-if="batchDialogMode === 'recognize' && batchProgress === 0" type="primary" @click="startBatchRecognize" :loading="batchProcessing" :disabled="!batchForm.folderPath || batchProcessing">
          开始识别
        </el-button>
        <el-button v-if="batchDialogMode === 'exportToFolder' && batchProgress === 0" type="primary" @click="startBatchExportToFolder" :loading="batchProcessing" :disabled="!exportFolderForm.displayPath || batchProcessing">
          开始导出
        </el-button>
        <el-button v-if="batchDialogMode === 'exportToLocalFolder' && batchProgress === 0" type="primary" @click="startBatchExportToLocalFolder" :loading="batchProcessing" :disabled="!exportLocalFolderForm.displayPath || batchProcessing">
          开始导出
        </el-button>
      </div>
    </el-dialog>
    
    <!-- 文件夹统计对话框 -->
    <el-dialog title="📁 文件夹详细统计" :visible.sync="folderStatsDialogVisible" width="900px">
      <!-- 统计摘要 -->
      <div style="margin-bottom: 20px; padding: 15px; background: linear-gradient(to right, #409EFF, #66B1FF); color: white; font-weight: bold; border-radius: 4px;">
        📁 检测到 {{ folderStats.length }} 个子文件夹，共 {{ totalFilesCount }} 个文件
        <span v-if="missingFolders.length > 0" style="margin-left: 10px; background: #F56C6C; padding: 4px 10px; border-radius: 3px; font-size: 12px;">
          🚫 {{ missingFolders.length }} 个未检测到
        </span>
        <span v-if="abnormalFolders.length > 0" style="margin-left: 10px; background: #E6A23C; padding: 4px 10px; border-radius: 3px; font-size: 12px;">
          ⚠️ {{ abnormalFolders.length }} 个异常
        </span>
      </div>
      
      <!-- 异常文件夹单独显示 -->
      <div v-if="abnormalFolders.length > 0 || missingFolders.length > 0" style="margin-bottom: 15px;">
        <!-- 缺失的文件夹 -->
        <div v-if="missingFolders.length > 0" style="margin-bottom: 10px;">
          <el-alert type="error" :closable="false">
            <template slot="title">
              <div style="display: flex; align-items: center; font-size: 14px;">
                <i class="el-icon-warning" style="font-size: 18px; margin-right: 8px;"></i>
                <strong>未检测到的文件夹（{{ missingFolders.length }} 个）</strong>
              </div>
            </template>
            <div style="margin-top: 10px; padding-bottom: 8px;">
              <div style="color: #606266; font-size: 12px; margin-bottom: 12px; line-height: 1.6;">
                以下序号的文件夹完全未被浏览器检测到，可能原因：文件夹不存在、为空或仅包含隐藏/系统文件
              </div>
              <div style="display: flex; flex-wrap: wrap; gap: 10px; align-items: center;">
                <el-tag v-for="num in missingFolders" :key="'missing-' + num" type="danger" effect="dark" size="medium" 
                  style="font-size: 15px; font-weight: bold; margin: 0;">
                  <i class="el-icon-close" style="margin-right: 5px;"></i>序号 {{ num }}
                </el-tag>
              </div>
            </div>
          </el-alert>
        </div>
        
        <!-- 异常文件夹 -->
        <div v-if="abnormalFolders.length > 0">
          <el-alert type="warning" :closable="false">
            <template slot="title">
              <div style="display: flex; align-items: center; font-size: 14px;">
                <i class="el-icon-warning-outline" style="font-size: 18px; margin-right: 8px;"></i>
                <strong>异常文件夹列表（{{ abnormalFolders.length }} 个）</strong>
              </div>
            </template>
            <div style="margin-top: 10px; padding-bottom: 8px;">
              <div style="color: #606266; font-size: 12px; margin-bottom: 12px;">
                以下文件夹已检测到但存在问题，请检查
              </div>
              <div style="display: flex; flex-wrap: wrap; gap: 10px; align-items: center;">
                <el-tag v-for="folder in abnormalFolders" :key="folder.folderName" 
                  :type="folder.fileCount === 0 ? 'danger' : 'warning'" size="medium" style="margin: 0;">
                  <i :class="folder.fileCount === 0 ? 'el-icon-folder-delete' : 'el-icon-warning'" style="margin-right: 5px;"></i>
                  <strong>{{ folder.folderName }}</strong>
                  <span style="margin-left: 6px; font-size: 12px; opacity: 0.9;">
                    {{ folder.fileCount === 0 ? '(空文件夹)' : `(${folder.fileCount}个文件，无关键词)` }}
                  </span>
                </el-tag>
              </div>
            </div>
          </el-alert>
        </div>
      </div>
      
      <!-- 文件夹详细表格 -->
      <el-table :data="folderStats" size="mini" stripe border max-height="500"
        :header-cell-style="{background: '#F5F7FA', color: '#606266', fontWeight: 'bold', textAlign: 'center'}">
        <el-table-column prop="folderName" label="文件夹" width="100" align="center" fixed>
          <template slot-scope="scope">
            <el-tag :type="scope.row.fileCount === 0 ? 'danger' : 'info'" size="small" style="font-weight: bold;">
              {{ scope.row.folderName }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="fileCount" label="文件数" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.fileCount > 0 ? 'success' : 'danger'" size="small">
              {{ scope.row.fileCount }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="hasKeyword" label="含关键词" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.hasKeyword ? 'success' : 'warning'" size="small">
              {{ scope.row.hasKeyword ? '✅' : '❌' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="files" label="文件列表" min-width="400">
          <template slot-scope="scope">
            <div style="font-size: 12px; color: #606266; line-height: 1.6;">
              <el-tag v-for="(file, index) in scope.row.files" :key="index"
                :type="file.includes(batchForm.imageKeyword) ? 'success' : ''" size="mini" style="margin: 2px;">
                {{ file }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
      </el-table>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="folderStatsDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DocumentList',
  data() {
    return {
      pageTitle: '证件信息列表', // 默认标题
      searchKeyword: '',
      confirmStatusFilter: 0, // 默认筛选未确认
      documentList: [],
      selectedRows: [],
      loading: false,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogMode: 'view', // view 或 edit
      saving: false,
      confirming: false, // 确认中状态
      reparsing: false,
      recognizing: false, // 识别中状态
      imageRotation: 0, // 图片旋转角度
      imageScale: 1.1, // 图片缩放比例
      imageScalePercent: 110, // 图片缩放百分比
      // 放大镜相关
      magnifierEnabled: false, // 是否启用放大镜
      showMagnifier: false, // 是否显示放大镜
      magnifierX: 0, // 放大镜 X 位置
      magnifierY: 0, // 放大镜 Y 位置
      magnifierSize: 150, // 放大镜尺寸
      magnifierZoom: 2.5, // 放大倍数
      imageRect: null, // 图片实际位置和尺寸
      replacingClipboard: false, // 替换剪贴板状态
      lastSelection: null, // 保存最后的选中信息
      currentDocument: {
        id: null,
        serialNumber: null,
        taxNumber: '',
        surname: '',
        givenName: '',
        middleName: '',
        fullName: '',
        dateOfBirth: '',
        address: '',
        rawText: '',
        confirmStatus: 1 // 默认已确认
      },
      // 批量识别
      batchDialogVisible: false,
      batchDialogMode: 'recognize', // 'recognize'、'export'、'exportToFolder' 或 'exportToLocalFolder'
      batchProcessing: false,
      batchProgress: 0,
      batchStatus: null,
      batchMessage: '',
      batchLogs: [],
      batchForm: {
        folderPath: '',
        imageKeyword: 'FRONT_ID'
      },
      selectedFiles: [], // 存储选中的文件
      folderStats: [], // 文件夹统计信息
      folderStatsDialogVisible: false, // 文件夹统计对话框显示状态
      // 批量导出JPG到指定目录
      exportFolderForm: {
        targetPath: '',
        displayPath: '',  // 显示的路径
        targetFolderHandle: null,  // 保存文件夹句柄
        dirHandle: null  // File System Access API 句柄
      },
      // 批量导出JPG到本地目录
      exportLocalFolderForm: {
        displayPath: '',  // 显示的路径
        dirHandle: null  // File System Access API 句柄
      },
      // 智能按钮布局
      visibleButtonCount: 5, // 默认显示的按钮数量
      permissionMap: {}, // 权限名称映射
      permissionSortMap: {}, // 权限排序映射
      autoUpperCase: true, // 自动转换为大写，默认开启
      useMonospaceFont: true // 使用等宽字体，默认开启
    }
  },
  mounted() {
    this.loadPageTitle()
    this.loadPermissionNames() // 加载权限名称
    this.getDocumentList()
    
    // 监听输入框的选中事件，保存选中信息
    document.addEventListener('selectionchange', this.saveSelection)
    
    // 监听窗口大小变化，重新计算按钮布局
    window.addEventListener('resize', this.calculateButtonLayout)
    this.$nextTick(() => {
      this.calculateButtonLayout()
    })
    
    // 调试: 显示当前用户权限
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        console.log('👤 当前用户:', user.username)
        console.log('🔑 用户权限:', user.permissions)
        
        // 检查关键权限
        const keyPermissions = ['document:add', 'document:edit', 'document:delete', 'document:list']
        keyPermissions.forEach(perm => {
          const has = user.permissions && user.permissions.includes(perm)
          console.log(`  ${has ? '✅' : '❌'} ${perm}: ${has}`)
        })
      } catch (e) {
        console.error('解析用户信息失败:', e)
      }
    } else {
      console.warn('⚠️ 未找到用户信息，所有按钮将默认显示')
    }
  },
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener('selectionchange', this.saveSelection)
    window.removeEventListener('resize', this.calculateButtonLayout)
  },
  computed: {
    // 总文件数
    totalFilesCount() {
      return this.folderStats.reduce((sum, folder) => sum + folder.fileCount, 0)
    },
    
    // 异常文件夹（空文件夹或缺少关键词的文件夹）
    abnormalFolders() {
      return this.folderStats.filter(folder => folder.fileCount === 0 || !folder.hasKeyword)
    },
    
    // 缺失的文件夹序号（理论上应该有但实际没有检测到）
    missingFolders() {
      if (this.folderStats.length === 0) return []
      
      // 提取所有文件夹的数字序号
      const existingNumbers = this.folderStats
        .map(f => this.extractNumber(f.folderName))
        .filter(n => n !== null)
        .sort((a, b) => a - b)
      
      if (existingNumbers.length === 0) return []
      
      // 找出最小和最大序号
      const minNum = existingNumbers[0]
      const maxNum = existingNumbers[existingNumbers.length - 1]
      
      // 检查从最小到最大之间缺失的序号
      const missing = []
      for (let i = minNum; i <= maxNum; i++) {
        if (!existingNumbers.includes(i)) {
          missing.push(i)
        }
      }
      
      return missing
    },
    
    // 所有按钮配置
    allButtons() {
      const buttons = [
        {
          key: 'add',
          permissionKey: 'document:add',
          type: 'primary',
          icon: 'el-icon-plus',
          label: this.getButtonName('document:add', '新增'),
          command: 'addDocument',
          onClick: this.addDocument,
          disabled: false,
          visible: this.hasPermission('document:add')
        },
        {
          key: 'batch-recognize',
          permissionKey: 'document:batch-recognize',
          type: 'success',
          icon: 'el-icon-upload',
          label: this.getButtonName('document:batch-recognize', '批量识别'),
          command: 'batchRecognize',
          onClick: this.showBatchDialog,
          disabled: false,
          visible: this.hasPermission('document:batch-recognize')
        },
        {
          key: 'batch-delete',
          permissionKey: 'document:batch-delete',
          type: 'danger',
          icon: 'el-icon-delete',
          label: this.getButtonName('document:batch-delete', `批量删除 (${this.selectedRows.length})`),
          command: 'batchDelete',
          onClick: this.batchDelete,
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-delete')
        },
        {
          key: 'batch-reparse',
          permissionKey: 'document:batch-reparse',
          type: 'warning',
          icon: 'el-icon-refresh',
          label: this.getButtonName('document:batch-reparse', `批量重新解析 (${this.selectedRows.length})`),
          command: 'batchReparse',
          onClick: this.batchReparse,
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-reparse')
        },
        {
          key: 'batch-confirm',
          permissionKey: 'document:batch-confirm',
          type: 'primary',
          icon: 'el-icon-check',
          label: this.getButtonName('document:batch-confirm', `批量设为已确认 (${this.selectedRows.length})`),
          command: 'batchConfirm',
          onClick: () => this.batchUpdateStatus(1),
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-confirm')
        },
        {
          key: 'batch-unconfirm',
          permissionKey: 'document:batch-confirm',
          type: 'info',
          icon: 'el-icon-close',
          label: this.getButtonName('document:batch-confirm', `批量设为未确认 (${this.selectedRows.length})`),
          command: 'batchUnconfirm',
          onClick: () => this.batchUpdateStatus(0),
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-confirm')
        },
        {
          key: 'export',
          permissionKey: 'document:export-excel',
          type: 'success',
          icon: 'el-icon-download',
          label: this.getButtonName('document:export-excel', '导出Excel'),
          command: 'exportDocuments',
          onClick: this.exportDocuments,
          disabled: false,
          visible: this.hasPermission('document:export-excel')
        },
        {
          key: 'batch-export-jpg',
          permissionKey: 'document:batch-export-jpg',
          type: 'warning',
          icon: 'el-icon-picture',
          label: this.getButtonName('document:batch-export-jpg', `批量导出JPG (${this.selectedRows.length})`),
          command: 'batchExportJpg',
          onClick: this.batchExportJpg,
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-export-jpg')
        },
        {
          key: 'batch-export-jpg-to-folder',
          permissionKey: 'document:batch-export-jpg-to-folder',
          type: 'success',
          icon: 'el-icon-folder-opened',
          label: this.getButtonName('document:batch-export-jpg-to-folder', `批量导出JPG到指定目录 (${this.selectedRows.length})`),
          command: 'batchExportJpgToFolder',
          onClick: this.batchExportJpgToFolder,
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-export-jpg-to-folder')
        },
        {
          key: 'batch-export-jpg-to-local-folder',
          permissionKey: 'document:batch-export-jpg-to-local-folder',
          type: 'primary',
          icon: 'el-icon-folder-add',
          label: this.getButtonName('document:batch-export-jpg-to-local-folder', `批量导出JPG到本地目录 (${this.selectedRows.length})`),
          command: 'batchExportJpgToLocalFolder',
          onClick: this.batchExportJpgToLocalFolder,
          disabled: this.selectedRows.length === 0,
          visible: this.hasPermission('document:batch-export-jpg-to-local-folder')
        }
      ]
      
      // 过滤不可见的按钮
      const visibleButtons = buttons.filter(btn => btn.visible)
      
      // 根据后端配置的sort值进行排序
      visibleButtons.sort((a, b) => {
        const sortA = this.permissionSortMap[a.permissionKey] || 999
        const sortB = this.permissionSortMap[b.permissionKey] || 999
        return sortA - sortB
      })
      
      return visibleButtons
    },
    // 可见按钮
    visibleButtons() {
      return this.allButtons.slice(0, this.visibleButtonCount)
    },
    // 下拉菜单按钮
    dropdownButtons() {
      return this.allButtons.slice(this.visibleButtonCount)
    },
    // 放大镜样式
    magnifierStyle() {
      return {
        left: `${this.magnifierX - this.magnifierSize / 2}px`,
        top: `${this.magnifierY - this.magnifierSize / 2}px`,
        width: `${this.magnifierSize}px`,
        height: `${this.magnifierSize}px`
      }
    },
    // 放大镜内图片样式
    magnifierImageStyle() {
      if (!this.imageRect) return {}
      
      // 计算鼠标相对于图片的位置
      const relativeX = this.magnifierX - this.imageRect.left
      const relativeY = this.magnifierY - this.imageRect.top
      
      // 计算放大后图片需要移动的距离
      const translateX = -(relativeX * this.magnifierZoom - this.magnifierSize / 2)
      const translateY = -(relativeY * this.magnifierZoom - this.magnifierSize / 2)
      
      return {
        transform: `translate(${translateX}px, ${translateY}px) scale(${this.magnifierZoom}) rotate(${this.imageRotation}deg)`,
        transformOrigin: 'top left',
        left: `${this.imageRect.left}px`,
        top: `${this.imageRect.top}px`,
        width: `${this.imageRect.width}px`,
        height: `${this.imageRect.height}px`
      }
    },
    // 上传请求头
    uploadHeaders() {
      const token = localStorage.getItem('admin_token')
      return {
        'Authorization': `Bearer ${token}`
      }
    }
  },
  watch: {
    // 监听allButtons变化，重新计算布局
    allButtons: {
      handler() {
        this.calculateButtonLayout()
      },
      deep: true
    },
    // 监听弹窗关闭，重置input值
    batchDialogVisible(newVal) {
      if (!newVal) {
        // 弹窗关闭后重置input的value
        this.$nextTick(() => {
          if (this.$refs.targetFolderInput) {
            this.$refs.targetFolderInput.value = ''
          }
          if (this.$refs.folderInput) {
            this.$refs.folderInput.value = ''
          }
        })
      }
    }
  },
  methods: {
    // 格式化日期时间
    formatDateTime(datetime) {
      if (!datetime) return ''
      const date = new Date(datetime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    // 加载权限名称
    async loadPermissionNames() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allPermissions = menus.permissions || []
          
          // 构建权限key到name和sort的映射
          this.permissionMap = {}
          this.permissionSortMap = {}
          allPermissions.forEach(p => {
            this.permissionMap[p.key] = p.name
            this.permissionSortMap[p.key] = p.sort || 999 // 默认排到最后
          })
        }
      } catch (error) {
        console.error('加载权限名称失败:', error)
      }
    },
    
    // 获取按钮名称（优先使用后端返回的名称）
    getButtonName(permissionKey, defaultName) {
      return this.permissionMap[permissionKey] || defaultName
    },
    
   // 计算按钮布局
    calculateButtonLayout() {
      this.$nextTick(() => {
        const container = this.$refs.buttonContainer
        if (!container || this.allButtons.length === 0) {
          this.visibleButtonCount = this.allButtons.length
          return
        }
        
        // 先尝试显示所有按钮
        this.visibleButtonCount = this.allButtons.length
        
        // 等待DOM更新后测量实际宽度
        this.$nextTick(() => {
          if (!container) return
          
          // 获取整个表单的宽度（搜索栏父容器）
          const form = container.closest('.search-form')
          if (!form) return
          
          const formWidth = form.offsetWidth
          
          // 计算搜索输入框和状态选择框的宽度
          const formItems = form.querySelectorAll('.el-form-item')
          let searchConditionsWidth = 0
          
          // 前两个是搜索条件，计算它们的宽度
          for (let i = 0; i < Math.min(2, formItems.length); i++) {
            searchConditionsWidth += formItems[i].offsetWidth
          }
          
          // 可用于按钮的宽度 = 表单总宽度 - 搜索条件宽度 - 预留间距
          const availableWidthForButtons = formWidth - searchConditionsWidth - 40
          
          // 获取按钮容器中的所有按钮
          const buttons = container.querySelectorAll('.el-button')
          if (buttons.length === 0) return
          
          // 计算所有按钮的总宽度（包括间距）
          let totalButtonsWidth = 0
          buttons.forEach(btn => {
            const style = window.getComputedStyle(btn)
            const width = btn.offsetWidth
            const marginLeft = parseFloat(style.marginLeft) || 0
            const marginRight = parseFloat(style.marginRight) || 0
            totalButtonsWidth += width + marginLeft + marginRight
          })
          
          // 如果所有按钮的宽度都在可用范围内，就不需要"更多操作"菜单
          if (totalButtonsWidth <= availableWidthForButtons) {
            this.visibleButtonCount = this.allButtons.length
            return
          }
          
          // 需要下拉菜单，预留"更多操作"按钮的宽度（约120px + 间距）
          const moreButtonWidth = 140
          const availableWidth = availableWidthForButtons - moreButtonWidth
          
          // 从后往前逐个隐藏按钮，直到总宽度小于可用宽度
          // 注意：buttons[0]是"搜索"按钮，buttons[1]开始才是allButtons中的按钮
          let currentWidth = totalButtonsWidth
          let visibleCount = this.allButtons.length
          
          // 从最后一个按钮开始隐藏
          for (let i = buttons.length - 1; i >= 1; i--) {
            if (currentWidth <= availableWidth) {
              break
            }
            
            const btn = buttons[i]
            const style = window.getComputedStyle(btn)
            const width = btn.offsetWidth
            const marginLeft = parseFloat(style.marginLeft) || 0
            const marginRight = parseFloat(style.marginRight) || 0
            currentWidth -= (width + marginLeft + marginRight)
            visibleCount--
          }
          
          // 至少显示1个按钮（除了搜索按钮）
          this.visibleButtonCount = Math.max(1, visibleCount)
        })
      })
    },
    
    // 处理更多操作菜单命令
    handleMoreCommand(command) {
      const button = this.allButtons.find(btn => btn.command === command)
      if (button && button.onClick) {
        button.onClick()
      }
    },
    
    // 动态加载页面标题
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    
    // 保存选中信息
    saveSelection() {
      const activeElement = document.activeElement
      
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        const start = activeElement.selectionStart
        const end = activeElement.selectionEnd
        
        if (start !== end) {
          this.lastSelection = {
            element: activeElement,
            start: start,
            end: end,
            text: activeElement.value.substring(start, end)
          }
        }
      }
    },
    
    // 动态计算姓名：姓名1 + "," + 姓名2 + " " + 姓名3
    updateFullName() {
      if (this.dialogMode === 'view') return
      
      const surname = this.currentDocument.surname || ''
      const givenName = this.currentDocument.givenName || ''
      const middleName = this.currentDocument.middleName || ''
      
      // 按照规则拼接：姓名1 + "," + 姓名2 + " " + 姓名3
      let fullName = ''
      if (surname) {
        fullName = surname
      }
      if (givenName) {
        fullName += (fullName ? ',' : '') + givenName
      }
      if (middleName) {
        fullName += (givenName ? ' ' : (fullName ? ',' : '')) + middleName
      }
      
      this.currentDocument.fullName = fullName
    },
    
    // 处理字段输入（实时转换，保持光标位置）
    handleFieldInput(fieldName, value) {
      if (!fieldName || this.dialogMode === 'view') return
      
      // 获取输入框元素和光标位置
      const refName = fieldName + 'Input'
      const inputRef = this.$refs[refName]
      if (!inputRef) return
      
      // 获取原生input元素
      const inputElement = inputRef.$el ? (inputRef.$el.querySelector('input') || inputRef.$el.querySelector('textarea')) : null
      if (!inputElement) return
      
      // 保存光标位置
      const cursorPosition = inputElement.selectionStart
      const oldValue = this.currentDocument[fieldName] || ''
      
      // 处理值
      let newValue = value
      if (this.autoUpperCase) {
        newValue = (newValue || '').toUpperCase()
      }
      
      // 更新值
      this.currentDocument[fieldName] = newValue
      
      // 标记为已确认
      this.markAsConfirmed()
      
      // 如果是姓名字段，更新完整姓名
      if (['surname', 'givenName', 'middleName'].includes(fieldName)) {
        this.$nextTick(() => {
          this.updateFullName()
        })
      }
      
      // 恢复光标位置
      this.$nextTick(() => {
        if (inputElement && cursorPosition !== null) {
          // 计算新的光标位置（如果有大小写转换，位置保持不变）
          const newPosition = cursorPosition
          inputElement.setSelectionRange(newPosition, newPosition)
        }
      })
    },
    
    // 处理姓名修改（失去焦点时处理，避免光标跳动）
    handleNameChange(fieldName) {
      if (fieldName && this.currentDocument[fieldName] !== undefined) {
        // 去除前后空格
        this.currentDocument[fieldName] = (this.currentDocument[fieldName] || '').trim()
        // 如果开启了自动转大写，将输入转为大写
        if (this.autoUpperCase) {
          this.currentDocument[fieldName] = this.currentDocument[fieldName].toUpperCase()
        }
      }
      // 更新完整姓名
      this.updateFullName()
    },
    
    // 处理其他字段失去焦点（姓名、地址等）
    handleFieldBlur(fieldName) {
      if (fieldName && this.currentDocument[fieldName] !== undefined) {
        // 去除前后空格
        this.currentDocument[fieldName] = (this.currentDocument[fieldName] || '').trim()
        // 如果开启了自动转大写，将输入转为大写
        if (this.autoUpperCase) {
          this.currentDocument[fieldName] = this.currentDocument[fieldName].toUpperCase()
        }
        // 如果是姓名字段，更新完整姓名
        if (['surname', 'givenName', 'middleName'].includes(fieldName)) {
          this.updateFullName()
        }
      }
    },
    
    // 标记为已确认（仅设置确认状态，不处理字段内容）
    markAsConfirmed() {
      if (this.dialogMode === 'edit') {
        this.currentDocument.confirmStatus = 1
      }
    },
    
    // 自动将确认状态改为已确认（保留用于税号等不需要特殊处理的字段）
    autoConfirm(fieldName) {
      if (fieldName && this.currentDocument[fieldName] !== undefined) {
        // 去除前后空格
        this.currentDocument[fieldName] = (this.currentDocument[fieldName] || '').trim()
        // 如果开启了自动转大写，将输入转为大写
        if (this.autoUpperCase) {
          this.currentDocument[fieldName] = this.currentDocument[fieldName].toUpperCase()
        }
      }
      if (this.dialogMode === 'edit') {
        this.currentDocument.confirmStatus = 1
      }
    },
    
    // 复制姓名到姓名1
    copyFullNameToSurname() {
      if (this.dialogMode === 'view') return
      
      const fullName = this.currentDocument.fullName || ''
      if (!fullName) {
        this.$message.warning('姓名为空，无法复制')
        return
      }
      
      this.currentDocument.surname = fullName
      this.markAsConfirmed()
      this.$message.success('已将姓名复制到姓名1')
    },
    
    // 清理地址开头的AODRESS（OCR识别错误）
    cleanAddress(address) {
      if (!address) return ''
      // 去掉开头的 AODRESS 或 ADDRESS（不区分大小写）
      return address.replace(/^(AODRESS|ADDRESS)\s*/i, '').trim()
    },
    
    // 获取证件列表
    async getDocumentList() {
      this.loading = true
      
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.get('/admin/ocr/document/page', {
          params: {
            pageNum: this.pagination.pageNum,
            pageSize: this.pagination.pageSize,
            keyword: this.searchKeyword,
            confirmStatus: this.confirmStatusFilter
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          const page = response.data.data
          // 使用 Vue.set 或直接赋值，避免不必要的深度对比
          this.documentList = page.records || []
          this.pagination.total = page.total || 0
        } else {
          this.$message.error(response.data.msg || '获取数据失败')
        }
      } catch (error) {
        console.error('获取数据失败', error)
        this.$message.error('获取数据失败：' + (error.response?.data?.msg || error.message))
      } finally {
        this.loading = false
      }
    },
    
    // 查看证件
    async viewDocument(row) {
      this.dialogMode = 'view'
      try {
        const token = localStorage.getItem('admin_token')
        const resp = await axios.get(`/admin/ocr/document/get/${row.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          this.currentDocument = resp.data.data
        } else {
          this.currentDocument = { ...row }
        }
      } catch (e) {
        this.currentDocument = { ...row }
      }
      // 清理地址开头的AODRESS
      this.currentDocument.address = this.cleanAddress(this.currentDocument.address)
      this.imageRotation = 0 // 重置旋转角度
      this.imageScale = 1.1 // 重置缩放比例
      this.imageScalePercent = 110 // 重置缩放百分比
      this.dialogVisible = true
    },
    
    // 新增证件
    addDocument() {
      this.dialogMode = 'edit'
      this.currentDocument = {
        id: null,
        serialNumber: '',
        taxNumber: '',
        fullName: '',
        dateOfBirth: '',
        address: '',
        confirmStatus: 1,
        imageUrl: '',
        rawText: ''
      }
      this.imageRotation = 0
      this.imageScale = 1.1
      this.imageScalePercent = 110
      this.dialogVisible = true
    },
    
    // 编辑证件
    async editDocument(row) {
      this.dialogMode = 'edit'
      try {
        const token = localStorage.getItem('admin_token')
        const resp = await axios.get(`/admin/ocr/document/get/${row.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          this.currentDocument = resp.data.data
        } else {
          this.currentDocument = { ...row }
        }
      } catch (e) {
        this.currentDocument = { ...row }
      }
      // 清理地址开头的AODRESS
      this.currentDocument.address = this.cleanAddress(this.currentDocument.address)
      this.imageRotation = 0 // 重置旋转角度
      this.imageScale = 1.1 // 重置缩放比例
      this.imageScalePercent = 110 // 重置缩放百分比
      // 打开编辑时默认设置为已确认
      if (this.currentDocument.confirmStatus === undefined || this.currentDocument.confirmStatus === null) {
        this.currentDocument.confirmStatus = 1
      }
      this.dialogVisible = true
    },
    
    // 旋转图片
    rotateImage(degree) {
      this.imageRotation = (this.imageRotation + degree) % 360
    },
    
    // 重置旋转
    resetRotation() {
      this.imageRotation = 0
    },
    
    // 处理缩放改变
    handleScaleChange(value) {
      this.imageScale = value / 100
    },
    
    // 重置缩放
    resetScale() {
      this.imageScalePercent = 110
      this.imageScale = 1.1
    },
    
    // 切换放大镜
    toggleMagnifier() {
      this.magnifierEnabled = !this.magnifierEnabled
      if (!this.magnifierEnabled) {
        this.showMagnifier = false
      }
    },
    
    // 调整放大镜大小
    adjustMagnifierSize(delta) {
      const newSize = this.magnifierSize + delta
      if (newSize >= 100 && newSize <= 300) {
        this.magnifierSize = newSize
      }
    },
    
    // 调整放大倍数
    adjustMagnifierZoom(delta) {
      const newZoom = this.magnifierZoom + delta
      if (newZoom >= 1.5 && newZoom <= 5) {
        this.magnifierZoom = newZoom
      }
    },
    
    // 处理鼠标移动
    handleMouseMove(event) {
      if (!this.magnifierEnabled) return
      
      // 获取图片元素的实际位置和尺寸
      const imageElement = this.$refs.documentImage?.$el?.querySelector('img')
      if (!imageElement) return
      
      const imageRect = imageElement.getBoundingClientRect()
      const wrapperRect = event.currentTarget.getBoundingClientRect()
      
      // 计算鼠标相对于包装器的位置
      const mouseX = event.clientX - wrapperRect.left
      const mouseY = event.clientY - wrapperRect.top
      
      // 计算图片相对于包装器的位置
      const imageOffsetX = imageRect.left - wrapperRect.left
      const imageOffsetY = imageRect.top - wrapperRect.top
      
      // 保存图片信息供计算属性使用
      this.imageRect = {
        left: imageOffsetX,
        top: imageOffsetY,
        width: imageRect.width,
        height: imageRect.height
      }
      
      // 设置放大镜位置（相对于包装器）
      this.magnifierX = mouseX
      this.magnifierY = mouseY
    },
    
    // 保存编辑
    // 确认证件
    async confirmDocument() {
      if (!this.currentDocument.id) {
        this.$message.error('证件ID不存在')
        return
      }
      
      this.confirming = true
      
      try {
        const token = localStorage.getItem('admin_token')
        
        // 设置为已确认
        this.currentDocument.confirmStatus = 1
        
        // 保存到数据库
        const response = await axios({
          method: 'put',
          url: '/admin/ocr/document/update',
          data: this.currentDocument,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          this.$message.success('已确认并保存成功')
          // 立即关闭弹窗，不等待列表刷新
          this.dialogVisible = false
          this.confirming = false
          // 异步刷新列表，不阻塞用户操作
          this.getDocumentList()
        } else {
          this.$message.error(response.data.msg || '保存失败')
          this.confirming = false
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败：' + (error.response?.data?.msg || error.message))
        this.confirming = false
      }
    },
    
    async saveDocumentEdit() {
      this.saving = true
      
      try {
        const token = localStorage.getItem('admin_token')
        
        // 根据是否有id判断是新增还是编辑
        const isAdd = !this.currentDocument.id
        const url = isAdd ? '/admin/ocr/document/add' : '/admin/ocr/document/update'
        const method = isAdd ? 'post' : 'put'
        
        const response = await axios({
          method: method,
          url: url,
          data: this.currentDocument,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          this.$message.success(isAdd ? '新增成功' : '更新成功')
          // 立即关闭弹窗，不等待列表刷新
          this.dialogVisible = false
          this.saving = false
          // 异步刷新列表，不阻塞用户操作
          this.getDocumentList()
        } else {
          this.$message.error(response.data.msg || (isAdd ? '新增失败' : '更新失败'))
          this.saving = false
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败：' + (error.response?.data?.msg || error.message))
        this.saving = false
      }
    },
    
    // 重新解析证件信息
    async reparseDocument() {
      if (!this.currentDocument.id) {
        this.$message.error('证件ID不存在')
        return
      }
      
      if (!this.currentDocument.rawText || this.currentDocument.rawText.trim() === '') {
        this.$message.error('原始文本为空，无法重新解析')
        return
      }
      
      this.$confirm('确认重新解析此证件信息？当前编辑的内容将被覆盖。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.reparsing = true
        
        try {
          const token = localStorage.getItem('admin_token')
          const response = await axios.post(
            `/admin/ocr/document/reparse/${this.currentDocument.id}`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          )
          
          if (response.data.code === 200) {
            this.$message.success('重新解析成功')
            // 更新当前表单数据
            const updatedData = response.data.data
            // 清理地址开头的AODRESS
            if (updatedData.address) {
              updatedData.address = this.cleanAddress(updatedData.address)
            }
            Object.assign(this.currentDocument, updatedData)
            // 刷新列表
            this.getDocumentList()
          } else {
            this.$message.error(response.data.msg || '重新解析失败')
          }
        } catch (error) {
          console.error('重新解析失败', error)
          this.$message.error('重新解析失败：' + (error.response?.data?.msg || error.message))
        } finally {
          this.reparsing = false
        }
      }).catch(() => {})
    },
    
    // 删除证件
    deleteDocument(row) {
      this.$confirm('确定要删除这条证件信息吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const token = localStorage.getItem('admin_token')
          const response = await axios.delete(`/admin/ocr/document/delete/${row.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.data.code === 200) {
            this.$message.success('删除成功')
            this.getDocumentList()
          } else {
            this.$message.error(response.data.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除失败', error)
          this.$message.error('删除失败：' + (error.response?.data?.msg || error.message))
        }
      }).catch(() => {})
    },
    
    // 本地图片上传(转为base64)
    async handleLocalImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        this.$message.error('只能上传图片文件！')
        return
      }
      
      // 验证文件大小(10MB)
      if (file.size / 1024 / 1024 > 10) {
        this.$message.error('图片大小不能超过 10MB！')
        return
      }
      
      try {
        // 转换为base64
        const base64 = await this.fileToBase64(file)
        this.currentDocument.imageUrl = base64
        this.$message.success('图片上传成功')
        
        // 清空input，允许重复选择同一文件
        event.target.value = ''
      } catch (error) {
        console.error('图片转换失败:', error)
        this.$message.error('图片上传失败')
      }
    },
    
    // 识别当前图片
    async recognizeCurrentImage() {
      if (!this.currentDocument.imageUrl) {
        this.$message.warning('请先上传图片')
        return
      }
      
      this.recognizing = true
      
      try {
        const token = localStorage.getItem('admin_token')
        
        // 如果是base64图片，需要转换为Blob
        let formData = new FormData()
        
        if (this.currentDocument.imageUrl.startsWith('data:')) {
          // base64图片
          const blob = this.base64ToBlob(this.currentDocument.imageUrl)
          formData.append('file', blob, 'image.jpg')
        } else {
          // URL图片，需要先下载
          const response = await fetch(this.getImageUrl(this.currentDocument.imageUrl))
          const blob = await response.blob()
          formData.append('file', blob, 'image.jpg')
        }
        
        this.$message.info('正在识别图片...')
        
        const ocrResponse = await axios.post(
          '/admin/ocr/recognize/document',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          }
        )
        
        if (ocrResponse.data.code === 200) {
          const ocrResult = ocrResponse.data.data
          
          // 回填识别结果
          if (ocrResult.structuredData) {
            const data = ocrResult.structuredData
            this.currentDocument.taxNumber = data['税号'] || this.currentDocument.taxNumber
            this.currentDocument.surname = data['SURNAME'] || this.currentDocument.surname
            this.currentDocument.givenName = data['GIVEN NAME'] || this.currentDocument.givenName
            this.currentDocument.middleName = data['MIDDLE NAME'] || this.currentDocument.middleName
            this.currentDocument.fullName = data['姓名'] || this.currentDocument.fullName
            this.currentDocument.dateOfBirth = data['出生日期'] || this.currentDocument.dateOfBirth
            
            // 清理地址开头的AODRESS
            if (data['地址']) {
              this.currentDocument.address = this.cleanAddress(data['地址'])
            }
            
            // 更新原始文本
            if (ocrResult.fullText) {
              this.currentDocument.rawText = ocrResult.fullText
            }
            
            // 自动设置为已确认
            this.currentDocument.confirmStatus = 1
            
            this.$message.success('识别成功，已自动回填表单')
          } else {
            this.$message.warning('识别成功，但未获取到结构化数据')
          }
        } else {
          this.$message.error(ocrResponse.data.msg || '识别失败')
        }
      } catch (error) {
        console.error('识别失败', error)
        this.$message.error('识别失败：' + (error.response?.data?.msg || error.message))
      } finally {
        this.recognizing = false
      }
    },
    
    // base64转换为Blob
    base64ToBlob(base64) {
      const arr = base64.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new Blob([u8arr], { type: mime })
    },
    
    // 导出Excel
    async exportDocuments() {
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.get('/admin/ocr/document/export', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        })
        
        // 创建下载链接
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // 从响应头获取文件名，或使用默认文件名
        const contentDisposition = response.headers['content-disposition']
        let fileName = '证件信息.xlsx'
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\.xlsx\1/)
          if (matches && matches[2]) {
            fileName = decodeURIComponent(matches[2]) + '.xlsx'
          }
        }
        
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败', error)
        this.$message.error('导出失败：' + (error.response?.data?.msg || error.message))
      }
    },
    
    // 导出单个JPG图片
    async exportJpg(row) {
      try {
        this.$message.info('正在生成JPG图片...')
        const token = localStorage.getItem('admin_token')
        
        // 构建请求数据（使用证件信息）
        const requestData = {
          id: row.id,
          serialNumber: row.serialNumber,
          taxNumber: row.taxNumber,
          fullName: row.fullName,
          dateOfBirth: row.dateOfBirth,
          address: row.address
        }
        
        const response = await axios.post('/admin/ocr/document/export-jpg-with-template', requestData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        })
        
        // 创建下载链接
        const blob = new Blob([response.data], { type: 'image/jpeg' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${row.serialNumber}.jpg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$message.success('导出JPG图片成功')
      } catch (error) {
        console.error('导出JPG图片失败', error)
        this.$message.error('导出失败：' + (error.response?.data?.msg || error.message))
      }
    },
    
    // 批量导出JPG图片
    async batchExportJpg() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一条数据')
        return
      }
      
      this.$confirm(`确定要导出选中的 ${this.selectedRows.length} 条证件信息为JPG图片吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        // 使用批量识别的进度显示方式
        this.batchDialogMode = 'export' // 设置为导出模式
        this.batchDialogVisible = true
        this.batchProcessing = true
        this.batchProgress = 0
        this.batchStatus = null
        this.batchLogs = []
        this.batchMessage = '正在生成JPG图片...'
        
        this.addBatchLog('✨ 开始批量导出JPG图片任务')
        this.addBatchLog(`📊 共选中 ${this.selectedRows.length} 条证件信息`)
        this.addBatchLog('')
        
        try {
          const token = localStorage.getItem('admin_token')
          let total = this.selectedRows.length
          let success = 0
          let failed = 0
          const failedItems = []
          
          // 逐个生成JPG图片
          for (let i = 0; i < this.selectedRows.length; i++) {
            const row = this.selectedRows[i]
            
            this.addBatchLog(`[${i + 1}/${total}] 正在生成: 序号 ${row.serialNumber}`)
            
            try {
              // 构建请求数据
              const requestData = {
                id: row.id,
                serialNumber: row.serialNumber,
                taxNumber: row.taxNumber,
                fullName: row.fullName,
                dateOfBirth: row.dateOfBirth,
                address: row.address
              }
              
              this.addBatchLog(`  🖼️ 正在填充PDF模板...`)
              
              const response = await axios.post('/admin/ocr/document/export-jpg-with-template', requestData, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                responseType: 'blob'
              })
              
              this.addBatchLog(`  ✅ 生成成功`, 'success')
              success++
              
            } catch (error) {
              this.addBatchLog(`  ❌ 生成失败: ${error.response?.data?.msg || error.message}`, 'danger')
              failed++
              failedItems.push(row.serialNumber)
            }
            
            // 更新进度
            this.batchProgress = Math.round((i + 1) / total * 100)
            this.batchMessage = `正在生成: ${i + 1}/${total}`
            
            this.addBatchLog('')
          }
          
          // 完成后统一下载
          if (success > 0) {
            this.addBatchLog('📦 开始打包下载...')
            
            // 构建请求数据列表（只包含成功的）
            const documents = this.selectedRows
              .filter(row => !failedItems.includes(row.serialNumber))
              .map(row => ({
                id: row.id,
                serialNumber: row.serialNumber,
                taxNumber: row.taxNumber,
                fullName: row.fullName,
                dateOfBirth: row.dateOfBirth,
                address: row.address
              }))
            
            const response = await axios.post('/admin/ocr/document/batch-export-jpg-with-template', documents, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              responseType: 'blob'
            })
            
            // 创建下载链接
            const blob = new Blob([response.data], { type: 'application/zip' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            
            // 生成文件名
            const now = new Date()
            const fileName = `BIR_Export_${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}.zip`
            link.download = fileName
            
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            
            this.addBatchLog('  ✅ 下载成功', 'success')
          }
          
          // 完成
          this.batchStatus = failed === 0 ? 'success' : 'exception'
          this.batchMessage = `导出完成！总计: ${total}, 成功: ${success}, 失败: ${failed}`
          this.addBatchLog('')
          this.addBatchLog(`✨ 批量导出JPG完成！`, 'success')
          this.addBatchLog(`📊 总计: ${total}, 成功: ${success}, 失败: ${failed}`, 'success')
          
          if (failed === 0) {
            this.$message.success(`批量导出完成！成功 ${success} 个JPG图片（ZIP压缩包）`)
          } else {
            this.$message.warning(`批量导出完成！成功 ${success} 个，失败 ${failed} 个`)
          }
          
          // 自动关闭弹窗（延迟2秒让用户看到完成消息）
          setTimeout(() => {
            this.batchDialogVisible = false
            this.batchProcessing = false
          }, 2000)
          
        } catch (error) {
          console.error('批量导出JPG图片失败', error)
          this.batchStatus = 'exception'
          this.batchMessage = '导出失败！'
          this.addBatchLog(`❌ 批量导出失败: ${error.message}`, 'danger')
          this.$message.error('批量导出失败：' + (error.response?.data?.msg || error.message))
          this.batchProcessing = false
        }
      }).catch(() => {})
    },
    
    // 替换选中文本为Ñ
    replaceClipboardWithN() {
      this.replacingClipboard = true
      
      try {
        if (!this.lastSelection) {
          this.$message.warning('请先在输入框中选中需要替换的文本')
          this.replacingClipboard = false
          return
        }
        
        const { element, start, end, text } = this.lastSelection
        
        // 确认元素仍然存在且有效
        if (!element || !document.body.contains(element)) {
          this.$message.warning('请先在输入框中选中需要替换的文本')
          this.replacingClipboard = false
          return
        }
        
        const beforeText = element.value.substring(0, start)
        const afterText = element.value.substring(end)
        
        // 替换为Ñ
        const newValue = beforeText + 'Ñ' + afterText
        element.value = newValue
        
        // 设置光标位置在Ñ之后
        const newCursorPos = start + 1
        element.focus()
        element.setSelectionRange(newCursorPos, newCursorPos)
        
        // 触发input事件以更新Vue的双向绑定
        element.dispatchEvent(new Event('input', { bubbles: true }))
        
        // 手动同步到Vue数据
        this.syncInputToVue(element, newValue)
        
        this.$message.success({
          message: `已将"${text}"替换为: Ñ`,
          duration: 2000
        })
        
        // 清空保存的选中信息
        this.lastSelection = null
      } catch (error) {
        console.error('替换失败', error)
        this.$message.error('替换失败：' + error.message)
      } finally {
        this.replacingClipboard = false
      }
    },
    
    // 同步输入框值到Vue数据
    syncInputToVue(inputElement, newValue) {
      // 查找该输入框所在的表单项
      const formItemEl = inputElement.closest('.el-form-item')
      if (!formItemEl) return
      
      // 获取label文本来判断字段
      const labelEl = formItemEl.querySelector('.el-form-item__label')
      if (!labelEl) return
      
      const labelText = labelEl.textContent.trim()
      
      // 根据label更新对应的字段
      const fieldMap = {
        '税号': 'taxNumber',
        '姓氏': 'surname',
        '名字': 'givenName',
        '中间名': 'middleName',
        '完整姓名': 'fullName',
        '出生日期': 'dateOfBirth',
        '地址': 'address',
        '原始文本': 'rawText'
      }
      
      const fieldName = fieldMap[labelText]
      if (fieldName && this.currentDocument.hasOwnProperty(fieldName)) {
        this.$set(this.currentDocument, fieldName, newValue)
      }
    },
    
    // 显示批量识别弹窗
    showBatchDialog() {
      this.batchDialogMode = 'recognize' // 设置为识别模式
      this.batchDialogVisible = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchLogs = []
      this.batchForm.folderPath = ''
      this.batchForm.imageKeyword = 'FRONT_ID'
      this.selectedFiles = []
      this.folderStats = [] // 清空文件夹统计信息
    },
    
    // 获取批量弹窗标题
    getBatchDialogTitle() {
      if (this.batchDialogMode === 'export') {
        return '批量导出JPG图片'
      } else if (this.batchDialogMode === 'exportToFolder') {
        return '批量导出JPG到指定目录'
      } else if (this.batchDialogMode === 'exportToLocalFolder') {
        return '批量导出JPG到本地目录'
      }
      return '批量识别证件'
    },
    
    // 批量导出JPG到指定目录
    async batchExportJpgToFolder() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一条数据')
        return
      }
      
      this.batchDialogMode = 'exportToFolder'
      this.batchDialogVisible = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchLogs = []
      this.batchMessage = ''
      this.exportFolderForm.targetPath = ''
      this.exportFolderForm.displayPath = ''
      this.exportFolderForm.targetFolderHandle = null
      this.exportFolderForm.dirHandle = null
    },
    
    // 批量导出JPG到本地目录（逐个生成保存）
    async batchExportJpgToLocalFolder() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一条数据')
        return
      }
      
      this.batchDialogMode = 'exportToLocalFolder'
      this.batchDialogVisible = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchLogs = []
      this.batchMessage = ''
      this.exportLocalFolderForm.displayPath = ''
      this.exportLocalFolderForm.dirHandle = null
    },
    
    // 关闭批量对话框
    closeBatchDialog() {
      this.batchDialogVisible = false
      // 重置状态
      this.batchProgress = 0
      this.batchStatus = null
      this.batchLogs = []
      this.batchMessage = ''
      this.batchProcessing = false
      this.folderStats = [] // 清空文件夹统计信息
      this.selectedFiles = [] // 清空选中的文件
      this.batchForm.folderPath = '' // 清空文件夹路径
      this.exportFolderForm.displayPath = '' // 清空目标文件夹显示路径
      this.exportFolderForm.targetFolderHandle = null // 清空文件夹句柄
      this.exportFolderForm.dirHandle = null // 清空目录句柄
      this.exportLocalFolderForm.displayPath = '' // 清空本地文件夹显示路径
      this.exportLocalFolderForm.dirHandle = null // 清空本地目录句柄
      
      // 重置文件选择器，解决再次选择相同文件夹时无反应的问题
      this.$nextTick(() => {
        if (this.$refs.folderInput) {
          this.$refs.folderInput.value = ''
        }
        if (this.$refs.targetFolderInput) {
          this.$refs.targetFolderInput.value = ''
        }
      })
    },
    
    // 开始批量导出JPG到指定目录（下载ZIP并解压保存）
    async startBatchExportToFolder() {
      if (!this.exportFolderForm.displayPath) {
        this.$message.warning('请选择目标文件夹')
        return
      }
      
      // 在用户点击时立即验证权限
      if ('showDirectoryPicker' in window && this.exportFolderForm.dirHandle) {
        try {
          const permission = await this.exportFolderForm.dirHandle.requestPermission({ mode: 'readwrite' })
          if (permission !== 'granted') {
            this.$message.error('没有文件夹写入权限，请重新选择')
            return
          }
        } catch (error) {
          console.error('验证权限失败:', error)
          this.$message.error('验证权限失败，请重新选择文件夹')
          return
        }
      }
      
      this.batchProcessing = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchMessage = '准备开始...'
      
      this.addBatchLog('✨ 开始批量导出JPG图片到指定目录')
      this.addBatchLog(`💾 目标目录: ${this.exportFolderForm.displayPath}`)
      this.addBatchLog(`📊 共选中 ${this.selectedRows.length} 条证件信息`)
      this.addBatchLog('')
      
      try {
        const token = localStorage.getItem('admin_token')
        
        // 准备请求数据
        const documents = this.selectedRows.map(row => ({
          serialNumber: row.serialNumber,
          taxNumber: row.taxNumber,
          fullName: row.fullName,
          dateOfBirth: row.dateOfBirth,
          address: row.address
        }))
        
        this.addBatchLog('📤 正在请求后端生成ZIP文件...')
        
        // 调用后端批量导出接口
        const response = await this.$axios.post('/admin/ocr/document/batch-export-jpg-download', 
          { documents },
          {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${token}` },
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.lengthComputable) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                this.batchProgress = percentCompleted
                this.batchMessage = `正在下载ZIP: ${percentCompleted}%`
              }
            }
          }
        )
        
        this.addBatchLog('✅ ZIP文件下载完成', 'success')
        this.addBatchLog('📦 正在解压并保存文件...')
        this.batchMessage = '正在解压...'
        
        // 解压并保存ZIP文件
        await this.extractAndSaveZip(response.data)
        
        this.batchProgress = 100
        this.batchStatus = 'success'
        this.batchMessage = '导出完成！'
        this.addBatchLog(`✅ 导出完成！文件已保存到: ${this.exportFolderForm.displayPath}`, 'success')
        
        setTimeout(() => {
          this.batchProcessing = false
        }, 1500)
        
      } catch (error) {
        console.error('导出失败', error)
        this.batchProgress = 0
        this.batchStatus = 'exception'
        this.batchMessage = '导出失败'
        this.addBatchLog(`❌ 导出失败: ${error.response?.data?.msg || error.message}`, 'danger')
        this.batchProcessing = false
      }
    },
    
    // 开始批量导出JPG到本地目录（逐个生成保存）
    async startBatchExportToLocalFolder() {
      if (!this.exportLocalFolderForm.displayPath) {
        this.$message.warning('请选择目标文件夹')
        return
      }
      
      // 在用户点击时立即验证权限
      if ('showDirectoryPicker' in window && this.exportLocalFolderForm.dirHandle) {
        try {
          const permission = await this.exportLocalFolderForm.dirHandle.requestPermission({ mode: 'readwrite' })
          if (permission !== 'granted') {
            this.$message.error('没有文件夹写入权限，请重新选择')
            return
          }
        } catch (error) {
          console.error('验证权限失败:', error)
          this.$message.error('验证权限失败，请重新选择文件夹')
          return
        }
      }
      
      this.batchProcessing = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchMessage = '准备开始...'
      
      this.addBatchLog('✨ 开始批量导出JPG图片到本地目录')
      this.addBatchLog(`💾 目标目录: ${this.exportLocalFolderForm.displayPath}`)
      this.addBatchLog(`📊 共选中 ${this.selectedRows.length} 条证件信息`)
      this.addBatchLog('')
      
      try {
        const token = localStorage.getItem('admin_token')
        const total = this.selectedRows.length
        let success = 0
        let failed = 0
        
        // 逐个生成JPG图片并保存
        for (let i = 0; i < this.selectedRows.length; i++) {
          const row = this.selectedRows[i]
          const serialNumber = row.serialNumber
          
          this.batchMessage = `正在生成: ${i + 1}/${total} (序号 ${serialNumber})`
          this.batchProgress = Math.round((i / total) * 100)
          this.addBatchLog(`[${i + 1}/${total}] 正在生成: 序号 ${serialNumber}`)
          
          try {
            // 调用后端单个生成接口
            const response = await this.$axios.post('/admin/ocr/document/export-jpg-with-template', {
              serialNumber: row.serialNumber,
              taxNumber: row.taxNumber,
              fullName: row.fullName,
              dateOfBirth: row.dateOfBirth,
              address: row.address
            }, {
              responseType: 'blob',
              headers: { 'Authorization': `Bearer ${token}` }
            })
            
            // 保存到文件夹
            const jpgBlob = response.data
            const fileName = `${serialNumber}/${serialNumber}.jpg`
            await this.saveFileToLocalFolder(fileName, jpgBlob)
            
            success++
            this.addBatchLog(`  ✅ 生成并保存成功`, 'success')
          } catch (error) {
            failed++
            this.addBatchLog(`  ❌ 生成失败: ${error.response?.data?.msg || error.message}`, 'danger')
          }
          
          this.addBatchLog('')
        }
        
        this.batchProgress = 100
        this.batchStatus = 'success'
        this.batchMessage = '导出完成！'
        this.addBatchLog('')
        this.addBatchLog(`📊 总计: ${total}, 成功: ${success}, 失败: ${failed}`, 'success')
        this.addBatchLog(`✅ 导出完成！文件已保存到: ${this.exportLocalFolderForm.displayPath}`, 'success')
        
        setTimeout(() => {
          this.batchProcessing = false
        }, 1500)
        
      } catch (error) {
        console.error('导出失败', error)
        this.batchProgress = 0
        this.batchStatus = 'exception'
        this.batchMessage = '导出失败'
        this.addBatchLog(`❌ 导出失败: ${error.message}`, 'danger')
        this.batchProcessing = false
      }
    },
    
    // 触发目标文件夹选择
    async triggerTargetFolderSelect() {
      console.log('triggerTargetFolderSelect 被调用')
      
      // 优先使用现代浏览器的 File System Access API
      if ('showDirectoryPicker' in window) {
        try {
          const dirHandle = await window.showDirectoryPicker()
          this.exportFolderForm.dirHandle = dirHandle
          this.exportFolderForm.displayPath = dirHandle.name
          this.$message.success(`已选择文件夹: ${dirHandle.name}`)
          console.log('displayPath 已设置为:', this.exportFolderForm.displayPath)
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('选择文件夹失败:', error)
            this.$message.error('选择文件夹失败: ' + error.message)
          }
          // AbortError 表示用户取消选择，不显示错误
        }
      } else {
        // 降级方案：使用传统的 webkitdirectory
        console.log('targetFolderInput ref:', this.$refs.targetFolderInput)
        if (this.$refs.targetFolderInput) {
          this.$refs.targetFolderInput.click()
          console.log('已触发 click')
        } else {
          console.error('targetFolderInput ref 不存在！')
        }
      }
    },
    
    // 触发本地文件夹选择（逐个生成方式）
    async triggerLocalFolderSelect() {
      console.log('triggerLocalFolderSelect 被调用')
      
      // 优先使用现代浏览器的 File System Access API
      if ('showDirectoryPicker' in window) {
        try {
          const dirHandle = await window.showDirectoryPicker()
          this.exportLocalFolderForm.dirHandle = dirHandle
          this.exportLocalFolderForm.displayPath = dirHandle.name
          this.$message.success(`已选择文件夹: ${dirHandle.name}`)
          console.log('displayPath 已设置为:', this.exportLocalFolderForm.displayPath)
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('选择文件夹失败:', error)
            this.$message.error('选择文件夹失败: ' + error.message)
          }
          // AbortError 表示用户取消选择，不显示错误
        }
      } else {
        this.$message.warning('您的浏览器不支持此功能，请使用Chrome、Edge或其他现代浏览器')
      }
    },
    
    // 处理目标文件夹选择
    handleTargetFolderSelect(event) {
      console.log('handleTargetFolderSelect 被调用', event)
      const files = event.target.files
      console.log('files:', files)
      console.log('files.length:', files ? files.length : 0)
      
      if (!files || files.length === 0) {
        console.log('没有选择文件')
        return
      }
      
      // 获取文件夹名称
      if (files[0].webkitRelativePath) {
        const fullPath = files[0].webkitRelativePath
        console.log('fullPath:', fullPath)
        const pathParts = fullPath.split('/')
        const folderName = pathParts[0]
        console.log('folderName:', folderName)
        
        this.exportFolderForm.displayPath = folderName
        // 保存files对象以便后续使用
        this.exportFolderForm.targetFolderHandle = files
        
        console.log('displayPath 已设置为:', this.exportFolderForm.displayPath)
        this.$message.success(`已选择文件夹: ${folderName}`)
      } else {
        console.log('没有 webkitRelativePath')
      }
    },
    
    // 解压并保存ZIP文件
    async extractAndSaveZip(zipBlob) {
      // 使用JSZip库解压ZIP
      const JSZip = window.JSZip || await import('jszip').then(m => m.default)
      const zip = await JSZip.loadAsync(zipBlob)
      
      let successCount = 0
      let failCount = 0
      
      // 遍历ZIP中的所有文件
      for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
        if (zipEntry.dir) continue // 跳过目录
        
        try {
          // 获取文件内容
          const content = await zipEntry.async('blob')
          
          // 使用File System Access API保存文件
          await this.saveFileToFolder(relativePath, content)
          
          successCount++
          this.addBatchLog(`  ✅ ${relativePath}`, 'success')
        } catch (error) {
          console.error(`保存文件失败: ${relativePath}`, error)
          failCount++
          this.addBatchLog(`  ❌ ${relativePath}: ${error.message}`, 'danger')
        }
      }
      
      this.addBatchLog('')
      this.addBatchLog(`📊 总计: ${successCount + failCount} 个文件, 成功: ${successCount}, 失败: ${failCount}`)
    },
    
    // 保存文件到文件夹
    async saveFileToFolder(relativePath, content) {
      // 使用showDirectoryPicker API（现代浏览器支持）
      if ('showDirectoryPicker' in window && this.exportFolderForm.dirHandle) {
        // 权限已在startBatchExportToFolder中验证，直接使用
        const pathParts = relativePath.split('/')
        let currentDir = this.exportFolderForm.dirHandle
        
        // 创建子目录
        for (let i = 0; i < pathParts.length - 1; i++) {
          currentDir = await currentDir.getDirectoryHandle(pathParts[i], { create: true })
        }
        
        // 创建文件
        const fileName = pathParts[pathParts.length - 1]
        const fileHandle = await currentDir.getFileHandle(fileName, { create: true })
        const writable = await fileHandle.createWritable()
        await writable.write(content)
        await writable.close()
      } else {
        // 降级方案：直接下载所有文件
        const url = URL.createObjectURL(content)
        const a = document.createElement('a')
        a.href = url
        a.download = relativePath.replace(/\//g, '_') // 将路径中的/替换为_
        a.click()
        URL.revokeObjectURL(url)
      }
    },
    
    // 保存文件到本地文件夹（逐个生成方式）
    async saveFileToLocalFolder(relativePath, content) {
      // 使用showDirectoryPicker API（现代浏览器支持）
      if ('showDirectoryPicker' in window && this.exportLocalFolderForm.dirHandle) {
        const pathParts = relativePath.split('/')
        let currentDir = this.exportLocalFolderForm.dirHandle
        
        // 创建子目录
        for (let i = 0; i < pathParts.length - 1; i++) {
          currentDir = await currentDir.getDirectoryHandle(pathParts[i], { create: true })
        }
        
        // 创建文件
        const fileName = pathParts[pathParts.length - 1]
        const fileHandle = await currentDir.getFileHandle(fileName, { create: true })
        const writable = await fileHandle.createWritable()
        await writable.write(content)
        await writable.close()
      } else {
        // 降级方案：直接下载所有文件
        const url = URL.createObjectURL(content)
        const a = document.createElement('a')
        a.href = url
        a.download = relativePath.replace(/\//g, '_') // 将路径中的/替换为_
        a.click()
        URL.revokeObjectURL(url)
      }
    },
    
    // 触发文件夹选择
    triggerFolderSelect() {
      this.$refs.folderInput.click()
    },
    
    // 处理文件夹选择
    handleFolderSelect(event) {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }
      
      this.selectedFiles = Array.from(files)
      
      // 获取文件夹路径（从第一个文件的路径提取）
      if (files[0].webkitRelativePath) {
        const fullPath = files[0].webkitRelativePath
        const pathParts = fullPath.split('/')
        
        // 尝试从完整路径中提取更多层级信息
        // 构建显示路径：显示根文件夹名称，并统计子文件夹数量
        const rootFolder = pathParts[0]
        
        // 统计唯一的子文件夹（第二层）
        const subFolders = new Set()
        const folderFileMap = {} // 统计每个子文件夹的文件数和文件列表
        
        Array.from(files).forEach(file => {
          if (file.webkitRelativePath) {
            const parts = file.webkitRelativePath.split('/')
            if (parts.length >= 2) {
              const folderName = parts[1]
              const fileName = parts[parts.length - 1]
              subFolders.add(folderName)
              
              // 统计文件
              if (!folderFileMap[folderName]) {
                folderFileMap[folderName] = []
              }
              folderFileMap[folderName].push(fileName)
            }
          }
        })
        
        // 生成文件夹统计信息
        this.generateFolderStats(folderFileMap)
        
        // 打印详细信息到控制台
        console.log('📁 检测到的子文件夹:', Array.from(subFolders).sort())
        console.log('📊 每个文件夹的文件:', folderFileMap)
        
        // 显示格式：根文件夹名称 (包含X个子文件夹，共Y个文件)
        const displayPath = `${rootFolder} (包含${subFolders.size}个子文件夹，共${files.length}个文件)`
        this.batchForm.folderPath = displayPath
        // 删除选择文件夹后的提示弹窗
      }
    },
    
    // 生成文件夹统计信息
    generateFolderStats(folderFileMap) {
      const keyword = this.batchForm.imageKeyword || 'FRONT_ID'
      
      this.folderStats = Object.keys(folderFileMap)
        .sort((a, b) => {
          // 按数字排序
          const numA = this.extractNumber(a)
          const numB = this.extractNumber(b)
          if (numA !== null && numB !== null) {
            return numA - numB
          }
          return a.localeCompare(b)
        })
        .map(folderName => {
          const files = folderFileMap[folderName]
          const hasKeyword = files.some(fileName => fileName.includes(keyword))
          
          return {
            folderName,
            fileCount: files.length,
            hasKeyword,
            files: files.sort()
          }
        })
    },
    
    // 更新文件夹统计（当关键词改变时）
    updateFolderStats() {
      if (this.selectedFiles.length === 0) return
      
      // 重新组织文件
      const folderFileMap = {}
      this.selectedFiles.forEach(file => {
        if (file.webkitRelativePath) {
          const parts = file.webkitRelativePath.split('/')
          if (parts.length >= 2) {
            const folderName = parts[1]
            const fileName = parts[parts.length - 1]
            
            if (!folderFileMap[folderName]) {
              folderFileMap[folderName] = []
            }
            folderFileMap[folderName].push(fileName)
          }
        }
      })
      
      // 重新生成统计
      this.generateFolderStats(folderFileMap)
    },
    
    // 添加日志
    addBatchLog(message, type = 'primary') {
      console.log('addBatchLog 被调用:', message, 'type:', type)
      const now = new Date()
      const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`
      this.batchLogs.push({
        time,
        message,
        type
      })
      console.log('batchLogs.length:', this.batchLogs.length)
      console.log('batchLogs:', this.batchLogs)
      
      // 自动滚动到最后
      this.$nextTick(() => {
        const logContainer = document.querySelector('.el-timeline')
        if (logContainer) {
          logContainer.scrollTop = logContainer.scrollHeight
        }
      })
    },
    
    // 获取日志图标
    getLogIcon(log) {
      if (log.message.includes('✅') || log.message.includes('成功')) {
        return 'el-icon-success'
      } else if (log.message.includes('❌') || log.message.includes('失败')) {
        return 'el-icon-error'
      } else if (log.message.includes('✨') || log.message.includes('开始')) {
        return 'el-icon-star-on'
      } else if (log.message.includes('📁')) {
        return 'el-icon-folder'
      } else if (log.message.includes('🔍')) {
        return 'el-icon-search'
      } else if (log.message.includes('📊') || log.message.includes('📝')) {
        return 'el-icon-document'
      }
      return 'el-icon-info'
    },
    
    // 获取日志样式
    getLogStyle(log) {
      if (log.message.includes('✅') || log.type === 'success') {
        return 'color: #67C23A; font-weight: 500;'
      } else if (log.message.includes('❌') || log.type === 'danger') {
        return 'color: #F56C6C; font-weight: 500;'
      } else if (log.message.includes('✨')) {
        return 'color: #409EFF; font-weight: bold; font-size: 15px;'
      } else if (log.message.startsWith('  [')) {
        return 'font-family: monospace; font-size: 13px;'
      }
      return 'color: #606266;'
    },
    
    // 按文件夹组织文件
    organizeFilesByFolder() {
      const folderMap = {}
      
      this.selectedFiles.forEach(file => {
        if (file.webkitRelativePath) {
          const pathParts = file.webkitRelativePath.split('/')
          if (pathParts.length >= 2) {
            const folderName = pathParts[1] // 第二级目录（序号文件夹）
            if (!folderMap[folderName]) {
              folderMap[folderName] = []
            }
            folderMap[folderName].push(file)
          }
        }
      })
      
      return folderMap
    },
    
    // 提取序号
    extractNumber(str) {
      const match = str.match(/\d+/)
      return match ? parseInt(match[0], 10) : null
    },
    
    // 将文件转换为base64
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
    
    // 显示文件夹统计对话框
    showFolderStatsDialog() {
      console.log('folderStats:', this.folderStats)
      console.log('abnormalFolders:', this.abnormalFolders)
      console.log('missingFolders:', this.missingFolders)
      console.log('totalFilesCount:', this.totalFilesCount)
      this.folderStatsDialogVisible = true
    },
    
    // 开始批量识别
    async startBatchRecognize() {
      console.log('=== DocumentList: 开始批量识别 ===')
      console.log('batchForm.folderPath:', this.batchForm.folderPath)
      console.log('selectedFiles.length:', this.selectedFiles.length)
      
      if (!this.batchForm.folderPath && this.selectedFiles.length === 0) {
        console.log('没有选择文件夹，直接return')
        this.$message.warning('请选择文件夹')
        return
      }
      
      console.log('开始执行批量识别...')
      
      this.batchProcessing = true
      this.batchProgress = 0
      this.batchStatus = null
      this.batchLogs = []
      this.batchMessage = '正在处理文件...'
      
      // 生成批次号: yyyyMMddHHmmss
      const batchNumber = new Date().toISOString()
        .replace(/[-T:.Z]/g, '')
        .substring(0, 14)
      
      this.addBatchLog('✨ 开始批量识别任务')
      this.addBatchLog('📦 批次号: ' + batchNumber)
      this.addBatchLog('📁 文件夹: ' + this.batchForm.folderPath)
      this.addBatchLog('🔍 图片关键词: ' + (this.batchForm.imageKeyword || 'FRONT_ID'))
      this.addBatchLog('🎯 识别类型: 通用文字')
      
      try {
        // 按文件夹组织文件
        const folderMap = this.organizeFilesByFolder()
        console.log('folderMap:', folderMap)
        const folderNames = Object.keys(folderMap).sort((a, b) => {
          const numA = this.extractNumber(a)
          const numB = this.extractNumber(b)
          if (numA !== null && numB !== null) {
            return numA - numB
          }
          return a.localeCompare(b)
        })
        
        this.addBatchLog(`📊 共找到 ${folderNames.length} 个子文件夹`)
        this.addBatchLog('')
        
        console.log('folderNames:', folderNames)
        console.log('folderNames.length:', folderNames.length)
        
        const token = localStorage.getItem('admin_token')
        let total = 0
        let success = 0
        let failed = 0
        
        console.log('开始处理文件夹...')
        
        // 逐个处理文件夹
        for (let i = 0; i < folderNames.length; i++) {
          const folderName = folderNames[i]
          const files = folderMap[folderName]
          total++
          
          console.log(`\n=== 处理第 ${i + 1}/${folderNames.length} 个文件夹: ${folderName} ===`)
          console.log('文件数量:', files.length)
          
          // 提取序号
          const serialNumber = this.extractNumber(folderName) || total
          
          this.addBatchLog(`[${i + 1}/${folderNames.length}] 正在处理: ${folderName} (序号: ${serialNumber})`)
          
          // 更新进度（先更新，确保日志区域可见）
          this.batchProgress = Math.round(((i + 1) / folderNames.length) * 100)
          this.batchMessage = `正在处理: ${i + 1}/${folderNames.length}`
          console.log('更新进度:', this.batchProgress, 'batchProcessing:', this.batchProcessing)
          
          try {
            // 查找包含关键词的图片
            const targetFile = files.find(file => 
              file.name.includes(this.batchForm.imageKeyword || 'FRONT_ID')
            )
            
            if (!targetFile) {
              this.addBatchLog(`  ❌ 未找到包含 '${this.batchForm.imageKeyword}' 的图片`, 'danger')
              failed++
              continue
            }
            
            this.addBatchLog(`  🖼️ 找到图片: ${targetFile.name}`)
            
            console.log('准备发送OCR请求...')
            
            // 识别图片（不传递type，使用OCR配置的API类型）
            const formData = new FormData()
            formData.append('file', targetFile)
            // formData.append('type', 'Advanced')  // 不传递type，让后端根据配置决定
            
            this.addBatchLog(`  🔍 开始OCR识别...`)
            
            console.log('开始OCR请求...')
            
            let ocrResult = null
            try {
              console.log('发送POST请求到 /admin/ocr/recognize/document')
              const ocrResponse = await axios.post(
                '/admin/ocr/recognize/document',
                formData,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                  }
                }
              )
              
              console.log('OCR响应:', ocrResponse.data)
              
              // 判断响应是否成功
              if (ocrResponse.data.code !== 200) {
                // HTTP层面错误（code不是200）
                const errorMsg = ocrResponse.data.message || ocrResponse.data.msg || 'OCR识别失败'
                console.log('HTTP层面错误:', errorMsg)
                this.addBatchLog(`  ❌ OCR识别失败: ${errorMsg}`, 'danger')
                failed++
                continue
              }
              
              // 检查data是否为null
              if (!ocrResponse.data.data) {
                const errorMsg = ocrResponse.data.message || ocrResponse.data.msg || 'OCR识别返回数据为空'
                console.log('data为null:', errorMsg)
                this.addBatchLog(`  ❌ OCR识别失败: ${errorMsg}`, 'danger')
                failed++
                continue
              }
              
              // 检查业务层是否成功
              if (!ocrResponse.data.data.success) {
                const errorMsg = ocrResponse.data.data.errorMessage || ocrResponse.data.message || ocrResponse.data.msg || 'OCR识别失败'
                console.log('业务层面错误:', errorMsg)
                this.addBatchLog(`  ❌ OCR识别失败: ${errorMsg}`, 'danger')
                failed++
                continue
              }
              
              ocrResult = ocrResponse.data.data
              this.addBatchLog(`  ✅ OCR识别成功`)
            } catch (ocrError) {
              // 捕获axios请求异常（如500错误）
              console.error('OCR请求异常:', ocrError)
              console.log('ocrError.response:', ocrError.response)
              console.log('ocrError.response?.data:', ocrError.response?.data)
              
              const errorMsg = ocrError.response?.data?.message || ocrError.response?.data?.msg || ocrError.message || 'OCR识别失败'
              console.log('提取的错误信息:', errorMsg)
              
              this.addBatchLog(`  ❌ OCR识别失败: ${errorMsg}`, 'danger')
              failed++
              continue
            }
            
            // 如果识别失败，不继续后续处理
            if (!ocrResult) {
              continue
            }
            
            // 将图片转换为base64
            const imageBase64 = await this.fileToBase64(targetFile)
            
            // 准备保存数据
            const documentData = {
              serialNumber: serialNumber,
              batchNumber: batchNumber,  // 添加批次号
              taxNumber: '',
              surname: '',
              givenName: '',
              middleName: '',
              fullName: '',
              dateOfBirth: '',
              address: '',
              imageUrl: imageBase64,
              rawText: ocrResult.fullText || ''
            }
            
            if (ocrResult.structuredData) {
              documentData.taxNumber = ocrResult.structuredData['税号'] || ''
              documentData.surname = ocrResult.structuredData['SURNAME'] || ''
              documentData.givenName = ocrResult.structuredData['GIVEN NAME'] || ''
              documentData.middleName = ocrResult.structuredData['MIDDLE NAME'] || ''
              documentData.fullName = ocrResult.structuredData['姓名'] || ''
              documentData.dateOfBirth = ocrResult.structuredData['出生日期'] || ''
              documentData.address = ocrResult.structuredData['地址'] || ''
            }
            
            this.addBatchLog(`  💾 正在保存数据...`)
            
            // 保存证件信息
            const saveResponse = await axios.post(
              '/admin/ocr/document/save',
              documentData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            )
            
            if (saveResponse.data.code === 200) {
              this.addBatchLog(`  ✅ 保存成功`, 'success')
              success++
            } else {
              this.addBatchLog(`  ❌ 保存失败: ${saveResponse.data.msg}`, 'danger')
              failed++
            }
            
          } catch (error) {
            // 捕获其他处理错误（如保存错误、文件读取错误等）
            const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || '处理失败'
            this.addBatchLog(`  ❌ 处理失败: ${errorMsg}`, 'danger')
            failed++
          }
          
          this.addBatchLog('')
        }
        
        // 完成
        this.batchStatus = failed === 0 ? 'success' : 'exception'
        this.batchMessage = `处理完成！总计: ${total}, 成功: ${success}, 失败: ${failed}`
        this.addBatchLog('', 'success')
        this.addBatchLog(`✨ 批量识别完成！`, 'success')
        this.addBatchLog(`📊 总计: ${total}, 成功: ${success}, 失败: ${failed}`, 'success')
        
        if (failed === 0) {
          // 全部成功，不弹出提示
        } else {
          this.addBatchLog('', 'warning')
          this.addBatchLog('⚠️ 有失败记录，请查看上方日志了解详情', 'warning')
        }
        
        // 刷新列表
        this.getDocumentList()
        // 不弹出提示，让用户查看页面上的统计信息和日志
        
      } catch (error) {
        console.error('批量识别失败', error)
        this.batchStatus = 'exception'
        this.batchMessage = '处理失败！'
        const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || '批量识别失败'
        this.addBatchLog(`❌ 批量识别失败: ${errorMsg}`, 'danger')
        // 删除弹窗提示，错误信息已显示在页面上
      } finally {
        this.batchProcessing = false
      }
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.getDocumentList()
    },
    
    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    
    // 批量删除
    batchDelete() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一条数据')
        return
      }
      
      this.$confirm(`确定要删除选中的 ${this.selectedRows.length} 条证件信息吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const token = localStorage.getItem('admin_token')
          const deletePromises = this.selectedRows.map(row => 
            axios.delete(`/admin/ocr/document/delete/${row.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
          )
          
          await Promise.all(deletePromises)
          
          this.$message.success(`成功删除 ${this.selectedRows.length} 条数据`)
          this.selectedRows = []
          this.getDocumentList()
        } catch (error) {
          console.error('批量删除失败', error)
          this.$message.error('批量删除失败：' + (error.response?.data?.msg || error.message))
        }
      }).catch(() => {})
    },
    
    // 批量重新解析
    async batchReparse() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一条数据')
        return
      }
      
      this.$confirm(`确定要重新解析选中的 ${this.selectedRows.length} 条证件信息吗？\n\n注意：已确认状态的数据将被跳过`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const loading = this.$loading({
          lock: true,
          text: `正在重新解析 ${this.selectedRows.length} 条数据...`,
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        try {
          const token = localStorage.getItem('admin_token')
          const ids = this.selectedRows.map(row => row.id)
          
          const response = await axios.post(
            '/admin/ocr/document/batch-reparse',
            ids,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          )
          
          if (response.data.code === 200) {
            const result = response.data.data
            
            // 显示详细结果
            let message = `批量重新解析完成！\n总计: ${result.total}\n成功: ${result.success}\n失败: ${result.failed}`
            
            // 显示跳过的数量
            if (result.skipped > 0) {
              message += `\n跳过: ${result.skipped} (已确认)`
            }
            
            if (result.failed > 0 && result.errors && result.errors.length > 0) {
              message += '\n\n失败详情：\n' + result.errors.slice(0, 5).join('\n')
              if (result.errors.length > 5) {
                message += `\n... 还有 ${result.errors.length - 5} 条错误`
              }
            }
            
            this.$alert(message, '批量重新解析结果', {
              confirmButtonText: '确定',
              type: result.failed === 0 ? 'success' : 'warning'
            })
            
            // 清空选择并刷新列表
            this.selectedRows = []
            this.getDocumentList()
          } else {
            this.$message.error(response.data.msg || '批量重新解析失败')
          }
        } catch (error) {
          console.error('批量重新解析失败', error)
          this.$message.error('批量重新解析失败：' + (error.response?.data?.msg || error.message))
        } finally {
          loading.close()
        }
      }).catch(() => {})
    },
    
    // 批量更新确认状态
    async batchUpdateStatus(confirmStatus) {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一条数据')
        return
      }
      
      const statusText = confirmStatus === 1 ? '已确认' : '未确认'
      
      this.$confirm(`确定要将选中的 ${this.selectedRows.length} 条证件信息设为「${statusText}」状态吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const loading = this.$loading({
          lock: true,
          text: `正在更新 ${this.selectedRows.length} 条数据...`,
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        try {
          const token = localStorage.getItem('admin_token')
          const ids = this.selectedRows.map(row => row.id)
          
          const response = await axios.post(
            '/admin/ocr/document/batch-update-status',
            {
              ids: ids,
              confirmStatus: confirmStatus
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          )
          
          if (response.data.code === 200) {
            const result = response.data.data
            
            // 显示详细结果
            let message = `批量更新确认状态完成！\n总计: ${result.total}\n成功: ${result.success}\n失败: ${result.failed}`
            
            if (result.failed > 0 && result.errors && result.errors.length > 0) {
              message += '\n\n失败详情：\n' + result.errors.slice(0, 5).join('\n')
              if (result.errors.length > 5) {
                message += `\n... 还有 ${result.errors.length - 5} 条错误`
              }
            }
            
            this.$alert(message, '批量更新结果', {
              confirmButtonText: '确定',
              type: result.failed === 0 ? 'success' : 'warning'
            })
            
            // 清空选择并刷新列表
            this.selectedRows = []
            this.getDocumentList()
          } else {
            this.$message.error(response.data.msg || '批量更新失败')
          }
        } catch (error) {
          console.error('批量更新确认状态失败', error)
          this.$message.error('批量更新确认状态失败：' + (error.response?.data?.msg || error.message))
        } finally {
          loading.close()
        }
      }).catch(() => {})
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.pageNum = val
      this.getDocumentList()
    },
    
    // 获取图片URL
    getImageUrl(imageUrl) {
      if (!imageUrl) {
        return ''
      }
      
      // 如果是base64编码，直接返回
      if (imageUrl.startsWith('data:image/')) {
        return imageUrl
      }
      
      // 如果是完整URL，直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }
      
      // 其他情况返回空，显示默认图片
      return ''
    },
    
    // 图片加载错误处理
    handleImageError(event) {
      console.error('图片加载失败', event)
    },

    // 检查是否有权限
    hasPermission(permissionKey) {
      try {
        const userStr = localStorage.getItem('admin_user')
        if (!userStr) {
          // 如果没有登录信息，默认显示所有按钮
          return true
        }
        const user = JSON.parse(userStr)
        if (!user.permissions || !Array.isArray(user.permissions)) {
          // 如果没有权限信息，默认显示所有按钮
          return true
        }
        return user.permissions.includes(permissionKey)
      } catch (error) {
        console.error('检查权限失败:', error)
        // 出错时默认显示
        return true
      }
    }
  }
}
</script>

<style scoped>
.document-list {
  padding: 20px;
}

.box-card {
  margin-bottom: 20px;
}

.search-form {
  margin-bottom: 20px;
}

/* 按钮容器样式 */
.button-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}

/* 操作列按钮样式优化 */
.document-list >>> .operation-column .cell {
  white-space: nowrap;
  overflow: visible;
}

.document-list >>> .operation-column .el-button {
  margin: 2px;
  padding: 7px 10px;
}

/* 表格整体样式优化 */
.document-list >>> .el-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.document-list >>> .el-table {
  font-size: 14px;
}

.document-list >>> .el-table td,
.document-list >>> .el-table th {
  padding: 12px 0;
}

/* 编辑弹窗样式 */
.image-section {
  height: 100%;
}

.image-container {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 放大镜容器 */
.magnifier-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
}

/* 放大镜镜片 */
.magnifier-glass {
  position: absolute;
  border: 3px solid #409EFF;
  border-radius: 50%;
  cursor: none;
  pointer-events: none;
  z-index: 1000;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.magnifier-glass img {
  position: absolute;
  max-width: none;
  max-height: none;
  pointer-events: none;
}

.image-container >>> .el-image {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  border-radius: 4px;
}

.image-container >>> .el-image img {
  max-width: 100% !important;
  max-height: calc(70vh - 100px) !important;
  width: auto !important;
  height: auto !important;
  object-fit: contain !important;
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 380px;
  color: #909399;
}

.image-error i {
  font-size: 64px;
  margin-bottom: 10px;
}

.image-error p {
  font-size: 14px;
  margin: 0;
}

.no-image {
  width: 100%;
  height: 400px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  color: #909399;
}

.no-image i {
  font-size: 64px;
  margin-bottom: 10px;
}

.no-image p {
  font-size: 14px;
  margin: 0;
}

.form-section {
  height: 100%;
  overflow-y: auto;
  max-height: 85vh;
  padding-right: 10px;
}

.form-section >>> .el-form-item {
  margin-bottom: 15px;
}

/* 表单字段加粗显示，让数据更明显 */
.form-section >>> .el-input__inner {
  font-weight: 600;
  color: #303133;
  font-size: 15px;
}

.form-section >>> .el-textarea__inner {
  font-weight: 600;
  color: #303133;
  font-size: 15px;
}

/* 原始文本框使用灰色背景 */
.form-section >>> .el-form-item[prop="rawText"] .el-textarea__inner {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
  color: #606266;
  font-weight: normal;
}

.form-section >>> .el-input-number__decrease,
.form-section >>> .el-input-number__increase {
  color: #606266;
}

/* 滚动条样式 */
.form-section::-webkit-scrollbar {
  width: 6px;
}

.form-section::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 3px;
}

.form-section::-webkit-scrollbar-thumb:hover {
  background-color: #c0c4cc;
}

/* 弹窗内容区域 */
.document-list >>> .el-dialog__body {
  padding: 20px;
  max-height: calc(96vh - 120px);
  overflow-y: auto;
}

/* h3标题样式 */
h3 {
  font-size: 16px;
  font-weight: 600;
  border-left: 3px solid #409EFF;
  padding-left: 10px;
}

/* 等宽字体样式 - 应用于所有输入框 */
.monospace-font >>> .el-input__inner,
.monospace-font >>> .el-textarea__inner,
.monospace-font >>> .el-input-number__increase,
.monospace-font >>> .el-input-number__decrease {
  font-family: 'Consolas', 'Monaco', 'Courier New', 'Liberation Mono', monospace !important;
  font-size: 16px !important;
  line-height: 1.8 !important;
  letter-spacing: 0.5px !important;
  font-weight: bold !important;
}

/* 原始文本框保持等宽字体（无论开关状态） */
.form-section >>> .el-form-item[prop="rawText"] .el-textarea__inner {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace !important;
  font-size: 12px !important;
}

/* 复制姓名按钮样式 */
.copy-fullname-btn {
  width: 25px;
  height: 25px;
  padding: 0;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  background: #fff;
}

.copy-fullname-btn:hover {
  color: #409EFF;
  border-color: #409EFF;
  background: #ecf5ff;
}

.copy-fullname-btn:active {
  color: #3a8ee6;
  border-color: #3a8ee6;
}

/* 调整prepend插槽的内边距 */
.form-section >>> .el-input-group__prepend {
  padding: 0px 8px 0px 20px;
}

/* 移动端自适应 */
@media screen and (max-width: 480px) {
  .copy-fullname-btn {
    width: 24px;
    height: 24px;
    font-size: 10px;
  }
}
</style>
